import { useEffect, useState } from 'react';
import {
  Loader2, Plus, Trash2, Pencil, ArrowUp, ArrowDown, Settings as SettingsIcon,
  Check, X, Users, Clock, Copy, Lock, Unlock, Archive, RotateCcw, Sparkles,
  Key, Eye, EyeOff, FileText, MessageSquare, Save, Mail, KeyRound, Zap, ShieldCheck,
  Building2, Activity, ExternalLink,
} from 'lucide-react';
import { authApi } from '../api/auth';
import AutomationEditor from './settings/AutomationEditor';
import TwoFactorEditor from './settings/TwoFactorEditor';
import EmailLogEditor from './settings/EmailLogEditor';
import { replyTemplatesApi, type ReplyTemplate } from '../api/extras';
import type { TemplateRegistryItem } from '../api/app_settings';
import { aiPromptsApi, type AIPrompt } from '../api/ai_prompts';
import { appSettingsApi, type AppSettingItem } from '../api/app_settings';
import {
  optionsApi, invalidateOptions, type OptionItem, type OptionItemIn, type ValueType,
} from '../api/options';
import { staffApi, clientsApi, orgsApi, type ClientUser, type Organization, type StaffPatch } from '../api/organizations';
import { OrgCombobox } from '../components/OrgCombobox';
import { slaApi, fmtMinutes, type SLAPlan } from '../api/sla';
import { api } from '../api/client';
import { useAuth } from '../lib/auth';
import { Navigate } from 'react-router-dom';

const DICT_LABEL: Record<string, string> = {
  installation_type: 'Топология инсталляции',
  os: 'Операционные системы',
  office_package: 'Офисные пакеты',
  dlp: 'DLP',
  antivirus: 'Антивирус',
  sandbox: 'Sandbox / песочница',
  siem: 'SIEM',
  storage_type: 'Хранилище',
  auth_type: 'Тип аутентификации',
  auth_ldap: 'LDAP-каталоги',
  auth_sso: 'SSO-провайдеры',
  license_option: 'Опции лицензии',
};

// Порядок отображения dict_key в левой навигации.
const DICT_ORDER = [
  'installation_type', 'os', 'office_package', 'dlp', 'antivirus',
  'sandbox', 'siem', 'storage_type', 'auth_type', 'auth_ldap', 'auth_sso',
  'license_option',
];

export default function SettingsPage() {
  const { user } = useAuth();
  if (user?.type !== 'staff') return <Navigate to="/" replace />;

  // Запоминаем последнюю открытую вкладку в localStorage; default — Сотрудники.
  // Защита: если в storage оказалось значение для удалённой/redirect-вкладки —
  // откатываемся на 'staff'. Иначе settings навсегда «зависает» на редиректе.
  const VALID_TABS = new Set([
    'staff', 'clients', 'password', 'twofactor', 'automation', 'sla', 'ai',
    'integrations', 'templates', 'bot_templates', 'email_templates', 'email_log',
    'org_visibility', 'org_duplicates', 'audit', 'trash',
  ]);
  const [active, setActive] = useState<string>(() => {
    try {
      const saved = localStorage.getItem('sd.settings.tab');
      if (saved && (VALID_TABS.has(saved) || saved in DICT_LABEL)) return saved;
    } catch {}
    return 'staff';
  });
  useEffect(() => {
    try { localStorage.setItem('sd.settings.tab', active); } catch { /* noop */ }
  }, [active]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <SettingsIcon className="text-blue-400" size={20} />
        <h1 className="text-2xl font-bold text-white">Настройки</h1>
      </div>

      <div className="grid grid-cols-[240px_1fr] gap-4">
        <nav className="bg-slate-900 border border-slate-800 rounded-xl p-2 self-start">
          <div className="text-[10px] uppercase tracking-wider text-slate-600 px-3 py-1">Системное</div>
          <NavBtn id="staff" label="Сотрудники" icon={Users} active={active} onClick={setActive} />
          <NavBtn id="clients" label="Клиенты" icon={Users} active={active} onClick={setActive} />
          <NavBtn id="password" label="Пароль" icon={KeyRound} active={active} onClick={setActive} />
          <NavBtn id="twofactor" label="2FA" icon={ShieldCheck} active={active} onClick={setActive} />
          <NavBtn id="automation" label="Триггеры" icon={Zap} active={active} onClick={setActive} />
          <NavBtn id="sla" label="SLA-планы" icon={Clock} active={active} onClick={setActive} />
          <NavBtn id="ai" label="AI промпты" icon={Sparkles} active={active} onClick={setActive} />
          <NavBtn id="integrations" label="Интеграции" icon={Key} active={active} onClick={setActive} />
          <NavBtn id="templates" label="Шаблоны ответов" icon={FileText} active={active} onClick={setActive} />
          <NavBtn id="bot_templates" label="Шаблоны бота" icon={MessageSquare} active={active} onClick={setActive} />
          <NavBtn id="email_templates" label="Шаблоны писем" icon={Mail} active={active} onClick={setActive} />
          <NavBtn id="email_log" label="Журнал писем" icon={Mail} active={active} onClick={setActive} />
          <NavBtn id="org_visibility" label="Видимость орг-ций" icon={Building2} active={active} onClick={setActive} />
          <NavBtn id="org_duplicates" label="Дубликаты орг-ций" icon={Building2} active={active} onClick={setActive} />
          <NavBtn id="audit" label="Аналитика действий" icon={Activity} active={active} onClick={setActive} />
          {/* HDE coverage — отдельная страница, не вкладка. Без setActive — иначе
              после первого клика Settings навсегда «зависает» в редиректе. */}
          <button onClick={() => { try { localStorage.removeItem('sd.settings.tab'); } catch {} ; window.location.href = '/hde-coverage'; }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm rounded text-slate-300 hover:bg-slate-800">
            <Activity size={14} className="text-slate-500" />
            <span>HDE-импорт: отчёт</span>
            <ExternalLink size={10} className="ml-auto text-slate-600" />
          </button>
          <NavBtn id="trash" label="Корзина" icon={Archive} active={active} onClick={setActive} />
          <div className="text-[10px] uppercase tracking-wider text-slate-600 px-3 py-1 mt-2">Справочники инсталляции</div>
          {DICT_ORDER.map(k => (
            <NavBtn key={k} id={k} label={DICT_LABEL[k] || k} active={active} onClick={setActive} />
          ))}
        </nav>

        {active === 'staff' ? <StaffEditor /> :
         active === 'clients' ? <ClientsEditor /> :
         active === 'password' ? <PasswordEditor /> :
         active === 'twofactor' ? <TwoFactorEditor /> :
         active === 'automation' ? <AutomationEditor /> :
         active === 'sla' ? <SLAEditor /> :
         active === 'ai' ? <AIPromptsEditor /> :
         active === 'integrations' ? <IntegrationsEditor /> :
         active === 'templates' ? <ReplyTemplatesEditor /> :
         active === 'bot_templates' ? <TemplatesEditor category="bot" /> :
         active === 'email_templates' ? <TemplatesEditor category="email" /> :
         active === 'email_log' ? <EmailLogEditor /> :
         active === 'org_visibility' ? <OrgVisibilityEditor /> :
         active === 'org_duplicates' ? <OrgDuplicatesEditor /> :
         active === 'audit' ? <AuditEditor /> :
         active === 'trash' ? <TrashEditor /> :
         <DictionaryEditor key={active} dictKey={active} />}
      </div>
    </div>
  );
}

function NavBtn({ id, label, icon: Icon, active, onClick }:
  { id: string; label: string; icon?: any; active: string; onClick: (id: string) => void }) {
  return (
    <button onClick={() => onClick(id)}
      className={`w-full text-left px-3 py-2 rounded text-sm mb-0.5 transition flex items-center gap-2 ${
        active === id
          ? 'bg-blue-600 text-white font-bold'
          : 'text-slate-300 hover:bg-slate-800'
      }`}>
      {Icon && <Icon size={13} />} {label}
    </button>
  );
}

// ── Staff editor ─────────────────────────────────────────────────────────────
function StaffEditor() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [items, setItems] = useState<ClientUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setItems(await staffApi.list()); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl">
      <header className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
        <h2 className="font-bold text-white">Сотрудники Service Desk</h2>
        {isAdmin && (
          <button onClick={() => setShowAdd(true)}
            className="text-xs flex items-center gap-1 bg-blue-600 hover:bg-blue-500 text-white px-3 py-1.5 rounded">
            <Plus size={12} /> Создать
          </button>
        )}
      </header>
      <div className="p-3">
        {!isAdmin && (
          <p className="text-[11px] text-slate-500 mb-2">
            Создание/блокировка сотрудников — только для администраторов.
          </p>
        )}
        {loading ? (
          <Loader2 className="animate-spin text-blue-400 mx-auto my-4" />
        ) : items.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-6">Нет сотрудников</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[10px] text-slate-500 uppercase tracking-wider">
              <tr>
                <th className="text-left py-2 px-3">Имя</th>
                <th className="text-left py-2 px-3">Должность</th>
                <th className="text-left py-2 px-3">Email · Телефон</th>
                <th className="text-left py-2 px-3">Роль</th>
                <th className="text-left py-2 px-3">Линии</th>
                <th className="text-right py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(u => <StaffRow key={u.id} u={u} canEdit={isAdmin} onChanged={load} />)}
            </tbody>
          </table>
        )}
      </div>
      {showAdd && <StaffCreateModal onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); load(); }} />}
    </section>
  );
}

function StaffRow({ u, canEdit, onChanged }: { u: ClientUser; canEdit: boolean; onChanged: () => void }) {
  const [resetUrl, setResetUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const lines = u.support_lines || [];

  const toggleLine = async (n: number) => {
    if (!canEdit) return;
    const next = lines.includes(n) ? lines.filter(x => x !== n) : [...lines, n].sort();
    await staffApi.patch(u.id, { support_lines: next });
    onChanged();
  };
  const reset = async () => {
    if (!window.confirm(`Сгенерировать новую ссылку для ${u.email}?`)) return;
    setBusy(true);
    try {
      const r = await staffApi.resetPassword(u.id);
      setResetUrl(r.activation_url);
    } finally { setBusy(false); }
  };
  const toggle = async () => {
    setBusy(true);
    try { await staffApi.patch(u.id, { is_active: !u.is_active }); onChanged(); }
    finally { setBusy(false); }
  };
  const copy = () => {
    if (!resetUrl) return;
    navigator.clipboard.writeText(resetUrl).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <>
    <tr className="border-t border-slate-800">
      <td className="py-2 px-3 text-slate-200">
        {u.full_name || '—'}
        {!u.is_active && <span className="ml-2 text-[10px] text-red-400">disabled</span>}
      </td>
      <td className="py-2 px-3 text-slate-300 text-xs">{u.position || <span className="text-slate-600">—</span>}</td>
      <td className="py-2 px-3 text-slate-400 font-mono text-[11px]">
        <div>{u.email}</div>
        {u.phone && <div className="text-slate-500">{u.phone}</div>}
      </td>
      <td className="py-2 px-3 text-slate-300 text-xs">{u.role}</td>
      <td className="py-2 px-3">
        <div className="flex gap-1">
          {[1, 2, 3].map(n => {
            const on = lines.includes(n);
            return (
              <button key={n} onClick={() => toggleLine(n)} disabled={!canEdit}
                title={canEdit ? `Линия ${n}` : `Линия ${n} (требуются права admin)`}
                className={`w-7 h-6 rounded text-[10px] font-bold border transition ${
                  on
                    ? 'bg-blue-600 border-blue-500 text-white'
                    : 'bg-slate-900 border-slate-700 text-slate-500 hover:bg-slate-800'
                } ${!canEdit ? 'cursor-not-allowed opacity-50' : ''}`}>
                L{n}
              </button>
            );
          })}
          {lines.length === 0 && <span className="text-[10px] text-slate-600 ml-1">все</span>}
        </div>
      </td>
      <td className="py-2 px-3 text-right whitespace-nowrap">
        {canEdit && (
          <>
            <button onClick={() => setEditing(true)} disabled={busy} title="Редактировать"
                    className="p-1 text-slate-500 hover:text-blue-400">
              <Pencil size={12} />
            </button>
            <button onClick={reset} disabled={busy} title="Сбросить пароль (новая ссылка)"
                    className="p-1 text-slate-500 hover:text-blue-400">
              <RotateCcw size={12} />
            </button>
            <button onClick={toggle} disabled={busy}
                    title={u.is_active ? 'Заблокировать' : 'Разблокировать'}
                    className="p-1 text-slate-500 hover:text-yellow-400">
              {u.is_active ? <Lock size={12} /> : <Unlock size={12} />}
            </button>
          </>
        )}
        {resetUrl && (
          <div className="mt-1 flex items-center gap-1 justify-end">
            <span className="text-[10px] font-mono text-blue-300 truncate max-w-[280px]">{resetUrl}</span>
            <button onClick={copy} className="text-slate-500 hover:text-blue-400 p-0.5">
              {copied ? <Check size={11} /> : <Copy size={11} />}
            </button>
          </div>
        )}
      </td>
    </tr>
    {editing && (
      <StaffEditModal user={u} onClose={() => setEditing(false)}
                      onSaved={() => { setEditing(false); onChanged(); }} />
    )}
    </>
  );
}

// ── Password editor ─────────────────────────────────────────────────────────
function PasswordEditor() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  return (
    <div className="space-y-4">
      <MyPasswordCard />
      {isAdmin && <AdminSetStaffPasswordCard />}
    </div>
  );
}

function MyPasswordCard() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCur, setShowCur] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null); setOk(false);
    if (next.length < 6) { setErr('Минимум 6 символов'); return; }
    if (next !== confirm) { setErr('Новый пароль и подтверждение не совпадают'); return; }
    if (next === current) { setErr('Новый пароль совпадает с текущим'); return; }
    setBusy(true);
    try {
      await authApi.changeMyPassword(current, next);
      setOk(true);
      setCurrent(''); setNext(''); setConfirm('');
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setBusy(false); }
  };

  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl">
      <header className="px-5 py-3 border-b border-slate-800">
        <h2 className="font-bold text-white">Мой пароль</h2>
        <p className="text-[11px] text-slate-500 mt-0.5">
          Смена пароля для своей учётной записи. После сохранения текущая сессия не разлогинивается.
        </p>
      </header>
      <form onSubmit={submit} className="p-5 space-y-3 max-w-md">
        <Field label="Текущий пароль">
          <div className="relative">
            <input type={showCur ? 'text' : 'password'} required value={current}
              onChange={e => setCurrent(e.target.value)}
              autoComplete="current-password"
              className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 pr-9 text-sm text-white font-mono focus:border-blue-500 outline-none" />
            <button type="button" onClick={() => setShowCur(s => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-blue-400">
              {showCur ? <EyeOff size={13} /> : <Eye size={13} />}
            </button>
          </div>
        </Field>
        <Field label="Новый пароль (мин. 6 символов)">
          <div className="relative">
            <input type={showNew ? 'text' : 'password'} required minLength={6} value={next}
              onChange={e => setNext(e.target.value)}
              autoComplete="new-password"
              className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 pr-9 text-sm text-white font-mono focus:border-blue-500 outline-none" />
            <button type="button" onClick={() => setShowNew(s => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-blue-400">
              {showNew ? <EyeOff size={13} /> : <Eye size={13} />}
            </button>
          </div>
        </Field>
        <Field label="Подтверждение">
          <input type={showNew ? 'text' : 'password'} required value={confirm}
            onChange={e => setConfirm(e.target.value)}
            autoComplete="new-password"
            className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white font-mono focus:border-blue-500 outline-none" />
        </Field>
        {err && <div className="bg-red-900/30 border border-red-800 text-red-300 px-3 py-2 rounded text-sm">{err}</div>}
        {ok && <div className="bg-emerald-900/30 border border-emerald-800 text-emerald-200 px-3 py-2 rounded text-sm flex items-center gap-2">
          <Check size={13} /> Пароль обновлён.
        </div>}
        <div className="pt-2">
          <button type="submit" disabled={busy}
            className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white px-4 py-1.5 rounded text-sm font-bold">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            Сменить пароль
          </button>
        </div>
      </form>
    </section>
  );
}

function AdminSetStaffPasswordCard() {
  const [staff, setStaff] = useState<ClientUser[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [pwd, setPwd] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => { staffApi.list().then(setStaff).catch(() => {}); }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null);
    if (!selectedId) { setMsg({ ok: false, text: 'Выберите сотрудника' }); return; }
    if (pwd.length < 6) { setMsg({ ok: false, text: 'Минимум 6 символов' }); return; }
    const target = staff.find(u => u.id === selectedId);
    if (!window.confirm(`Установить новый пароль для ${target?.email}? Сотрудник сможет войти с этим паролем сразу.`)) return;
    setBusy(true);
    try {
      await authApi.setStaffPassword(selectedId, pwd);
      setMsg({ ok: true, text: `Пароль для ${target?.email} установлен.` });
      setPwd('');
    } catch (e: any) {
      setMsg({ ok: false, text: e?.response?.data?.detail || e?.message || 'Ошибка' });
    } finally { setBusy(false); }
  };

  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl">
      <header className="px-5 py-3 border-b border-slate-800">
        <h2 className="font-bold text-white">Установить пароль сотруднику (admin)</h2>
        <p className="text-[11px] text-slate-500 mt-0.5">
          Прямая установка нового пароля без email-ссылки. Используйте, если сотрудник в офисе и
          сообщит пароль голосом. Альтернатива — кнопка «Сбросить пароль» в «Сотрудники» (отправит ссылку
          активации на email).
        </p>
      </header>
      <form onSubmit={submit} className="p-5 space-y-3 max-w-md">
        <Field label="Сотрудник">
          <select value={selectedId} onChange={e => setSelectedId(e.target.value)}
            className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none">
            <option value="">— выберите —</option>
            {staff.map(u => (
              <option key={u.id} value={u.id}>
                {u.full_name || u.email} · {u.email}{!u.is_active ? ' (disabled)' : ''}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Новый пароль (мин. 6 символов)">
          <div className="relative">
            <input type={show ? 'text' : 'password'} required minLength={6} value={pwd}
              onChange={e => setPwd(e.target.value)}
              autoComplete="new-password"
              className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 pr-9 text-sm text-white font-mono focus:border-blue-500 outline-none" />
            <button type="button" onClick={() => setShow(s => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-blue-400">
              {show ? <EyeOff size={13} /> : <Eye size={13} />}
            </button>
          </div>
        </Field>
        {msg && (
          <div className={`px-3 py-2 rounded text-sm ${
            msg.ok ? 'bg-emerald-900/30 border border-emerald-800 text-emerald-200'
                   : 'bg-red-900/30 border border-red-800 text-red-300'
          }`}>{msg.ok ? '✓ ' : '✗ '}{msg.text}</div>
        )}
        <div className="pt-2">
          <button type="submit" disabled={busy || !selectedId}
            className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white px-4 py-1.5 rounded text-sm font-bold">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <KeyRound size={13} />}
            Установить пароль
          </button>
        </div>
      </form>
    </section>
  );
}


function StaffEditModal({ user, onClose, onSaved }:
  { user: ClientUser; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<StaffPatch>({
    full_name: user.full_name || '',
    email: user.email,
    role: (user.role === 'admin' ? 'admin' : 'agent'),
    position: user.position || '',
    phone: user.phone || '',
    support_lines: user.support_lines || [],
    notify_new_tickets: !!user.notify_new_tickets,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const toggleLine = (n: number) => {
    setForm(f => {
      const cur = f.support_lines || [];
      const next = cur.includes(n) ? cur.filter(x => x !== n) : [...cur, n].sort();
      return { ...f, support_lines: next };
    });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await staffApi.patch(user.id, {
        full_name: form.full_name,
        email: form.email,
        role: form.role,
        position: form.position || null,
        phone: form.phone || null,
        support_lines: form.support_lines || [],
        notify_new_tickets: !!form.notify_new_tickets,
      });
      onSaved();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setBusy(false); }
  };

  return (
    <tr><td colSpan={6}><div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 overflow-y-auto py-10" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md mx-4 shadow-2xl"
           onClick={e => e.stopPropagation()}>
        <header className="p-4 border-b border-slate-800 flex justify-between">
          <h3 className="text-lg font-bold text-white">Редактировать сотрудника</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white">✕</button>
        </header>
        <form onSubmit={submit} className="p-4 space-y-3">
          <Field label="Имя">
            <input value={form.full_name || ''}
                   onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
                   className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none" />
          </Field>
          <Field label="Должность">
            <input value={form.position || ''}
                   onChange={e => setForm(f => ({ ...f, position: e.target.value }))}
                   placeholder="Например, Инженер 1-й линии"
                   className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none" />
          </Field>
          <Field label="Email">
            <input type="email" required value={form.email || ''}
                   onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                   className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white font-mono focus:border-blue-500 outline-none" />
          </Field>
          <Field label="Телефон">
            <input value={form.phone || ''}
                   onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                   placeholder="+7 (495) ..."
                   className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none" />
          </Field>
          <Field label="Роль">
            <select value={form.role}
                    onChange={e => setForm(f => ({ ...f, role: e.target.value as 'agent'|'admin' }))}
                    className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none">
              <option value="agent">Агент</option>
              <option value="admin">Администратор</option>
            </select>
          </Field>
          <Field label="Линии поддержки">
            <div className="flex gap-1.5 items-center">
              {[1, 2, 3].map(n => {
                const on = (form.support_lines || []).includes(n);
                return (
                  <button type="button" key={n} onClick={() => toggleLine(n)}
                    className={`w-12 h-8 rounded text-xs font-bold border transition ${
                      on
                        ? 'bg-blue-600 border-blue-500 text-white'
                        : 'bg-slate-950 border-slate-700 text-slate-400 hover:bg-slate-800'
                    }`}>
                    L{n}
                  </button>
                );
              })}
              <span className="ml-2 text-[11px] text-slate-500">
                {(form.support_lines?.length ?? 0) === 0
                  ? 'пусто = все линии'
                  : `закрывает ${(form.support_lines || []).map(n => 'L'+n).join(', ')}`}
              </span>
            </div>
          </Field>
          <Field label="Письма о новых тикетах">
            <button type="button"
              onClick={() => setForm(f => ({ ...f, notify_new_tickets: !f.notify_new_tickets }))}
              className={`w-full text-left px-3 py-2 rounded border text-sm transition ${
                form.notify_new_tickets
                  ? 'bg-blue-600/20 border-blue-500 text-blue-200'
                  : 'bg-slate-950 border-slate-700 text-slate-400 hover:bg-slate-800'
              }`}>
              <span className="font-bold">{form.notify_new_tickets ? 'Включены' : 'Выключены'}</span>
              <span className="block text-[11px] mt-0.5 opacity-80">
                {form.notify_new_tickets
                  ? 'Письмо о каждом новом тикете на своих линиях, а не только о назначенных лично'
                  : 'Приходят только письма о тикетах, назначенных лично'}
              </span>
            </button>
          </Field>
          {err && <div className="bg-red-900/30 border border-red-800 text-red-300 px-3 py-2 rounded text-sm">{err}</div>}
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-800 -mx-4 px-4 -mb-4 pb-4 mt-3">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded text-slate-300 text-sm">Отмена</button>
            <button type="submit" disabled={busy || !form.email}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 rounded text-white text-sm font-bold">
              {busy ? 'Сохраняю…' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div></td></tr>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">{label}</span>
      {children}
    </label>
  );
}

function StaffCreateModal({ onClose, onCreated }:
  { onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({
    email: '', full_name: '', role: 'agent' as 'agent' | 'admin',
    position: '', phone: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [activationUrl, setActivationUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const r = await staffApi.create(form);
      setActivationUrl(r.activation_url);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setBusy(false); }
  };
  const copy = () => {
    if (!activationUrl) return;
    navigator.clipboard.writeText(activationUrl).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 overflow-y-auto py-10" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md mx-4 shadow-2xl"
           onClick={e => e.stopPropagation()}>
        <header className="p-4 border-b border-slate-800 flex justify-between">
          <h3 className="text-lg font-bold text-white">Новый сотрудник</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white">✕</button>
        </header>
        {activationUrl ? (
          <div className="p-4 space-y-3">
            <p className="text-emerald-300 text-sm">Сотрудник создан.</p>
            <p className="text-xs text-slate-400">Перешлите ему ссылку для установки пароля (72 ч):</p>
            <div className="bg-slate-950 border border-slate-700 rounded px-3 py-2 text-xs font-mono text-blue-300 break-all">
              {activationUrl}
            </div>
            <div className="flex gap-2">
              <button onClick={copy} className="text-xs bg-slate-800 hover:bg-slate-700 text-slate-200 px-3 py-1.5 rounded">
                {copied ? 'Скопировано ✓' : 'Скопировать'}
              </button>
              <button onClick={onCreated} className="text-xs bg-blue-600 hover:bg-blue-500 text-white font-bold px-3 py-1.5 rounded ml-auto">
                Готово
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-4 space-y-3">
            <label className="block">
              <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Email</span>
              <input type="email" required value={form.email}
                     onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                     className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none" />
            </label>
            <label className="block">
              <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Имя</span>
              <input value={form.full_name}
                     onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
                     className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none" />
            </label>
            <label className="block">
              <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Должность</span>
              <input value={form.position}
                     onChange={e => setForm(f => ({ ...f, position: e.target.value }))}
                     placeholder="Например, Инженер 1-й линии"
                     className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none" />
            </label>
            <label className="block">
              <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Телефон</span>
              <input value={form.phone}
                     onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                     placeholder="+7 (495) ..."
                     className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none" />
            </label>
            <label className="block">
              <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Роль</span>
              <select value={form.role}
                      onChange={e => setForm(f => ({ ...f, role: e.target.value as any }))}
                      className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none">
                <option value="agent">Агент</option>
                <option value="admin">Администратор</option>
              </select>
            </label>
            {err && <div className="bg-red-900/30 border border-red-800 text-red-300 px-3 py-2 rounded text-sm">{err}</div>}
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-800 -mx-4 px-4 -mb-4 pb-4 mt-3">
              <button type="button" onClick={onClose} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded text-slate-300 text-sm">Отмена</button>
              <button type="submit" disabled={busy || !form.email}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 rounded text-white text-sm font-bold">
                {busy ? 'Создаю…' : 'Создать'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ── AI prompts editor ────────────────────────────────────────────────────────
function AIPromptsEditor() {
  const [items, setItems] = useState<AIPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState<string>('');

  const load = async () => {
    setLoading(true);
    try {
      const list = await aiPromptsApi.list();
      setItems(list);
      if (list.length && !activeKey) setActiveKey(list[0].key);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) {
    return <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
      <Loader2 className="animate-spin text-blue-400 mx-auto" />
    </div>;
  }
  if (!items.length) {
    return <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 text-sm text-slate-500">
      Нет AI-промптов.
    </div>;
  }

  const active = items.find(p => p.key === activeKey) || items[0];

  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl">
      <header className="px-5 py-3 border-b border-slate-800">
        <h2 className="font-bold text-white">AI промпты</h2>
        <p className="text-[11px] text-slate-500 mt-1">
          Текст системного и пользовательского промпта для каждого AI-сценария.
          Изменения применяются к следующим вызовам без перезапуска backend.
        </p>
      </header>
      <div className="grid grid-cols-[200px_1fr]">
        <nav className="border-r border-slate-800 p-2">
          {items.map(p => (
            <button key={p.key} onClick={() => setActiveKey(p.key)}
              className={`w-full text-left px-3 py-2 rounded text-sm mb-0.5 transition ${
                active.key === p.key ? 'bg-blue-600 text-white font-bold' : 'text-slate-300 hover:bg-slate-800'
              }`}>
              {p.name}
              <div className={`text-[10px] font-mono ${active.key === p.key ? 'text-blue-100' : 'text-slate-500'}`}>{p.key}</div>
            </button>
          ))}
        </nav>
        <AIPromptForm key={active.key} prompt={active} onSaved={load} />
      </div>
    </section>
  );
}

function AIPromptForm({ prompt, onSaved }: { prompt: AIPrompt; onSaved: () => void }) {
  const [form, setForm] = useState({
    name: prompt.name, description: prompt.description || '',
    system_prompt: prompt.system_prompt, user_template: prompt.user_template,
    model: prompt.model || '', temperature: prompt.temperature, max_tokens: prompt.max_tokens,
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      await aiPromptsApi.update(prompt.key, {
        name: form.name, description: form.description || null,
        system_prompt: form.system_prompt, user_template: form.user_template,
        model: form.model || null, temperature: Number(form.temperature),
        max_tokens: Number(form.max_tokens),
      } as any);
      setMsg('Сохранено');
      onSaved();
    } catch (e: any) {
      setMsg(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm(`Восстановить промпт «${prompt.name}» из встроенного дефолта?`)) return;
    setBusy(true); setMsg(null);
    try { await aiPromptsApi.reset(prompt.key); onSaved(); setMsg('Восстановлено из дефолта'); }
    catch (e: any) { setMsg(e?.response?.data?.detail || 'Ошибка'); }
    finally { setBusy(false); }
  };

  const ta = "w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-slate-200 font-mono focus:border-blue-500 outline-none";
  const inp = "bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm text-white focus:border-blue-500 outline-none";

  return (
    <div className="p-4 space-y-3">
      {prompt.description && (
        <p className="text-[11px] text-slate-500">{prompt.description}</p>
      )}
      {prompt.placeholders.length > 0 && (
        <p className="text-[11px] text-slate-500">
          Доступные плейсхолдеры в user-шаблоне:{' '}
          {prompt.placeholders.map(p => <code key={p} className="text-blue-300 mr-2">{`{${p}}`}</code>)}
        </p>
      )}

      <label className="block">
        <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Название</span>
        <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className={inp + " w-full"} />
      </label>
      <label className="block">
        <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Описание</span>
        <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className={inp + " w-full"} />
      </label>

      <label className="block">
        <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">System prompt</span>
        <textarea value={form.system_prompt} onChange={e => setForm(f => ({ ...f, system_prompt: e.target.value }))}
                  rows={12} className={ta} />
      </label>
      <label className="block">
        <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">User template</span>
        <textarea value={form.user_template} onChange={e => setForm(f => ({ ...f, user_template: e.target.value }))}
                  rows={6} className={ta} />
      </label>

      <div className="grid grid-cols-3 gap-3">
        <label className="block">
          <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Model (опц.)</span>
          <input value={form.model} placeholder="по умолчанию из env"
                 onChange={e => setForm(f => ({ ...f, model: e.target.value }))} className={inp + " w-full"} />
        </label>
        <label className="block">
          <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Temperature</span>
          <input type="number" step="0.1" min="0" max="2" value={form.temperature}
                 onChange={e => setForm(f => ({ ...f, temperature: Number(e.target.value) }))} className={inp + " w-full"} />
        </label>
        <label className="block">
          <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Max tokens</span>
          <input type="number" min="100" value={form.max_tokens}
                 onChange={e => setForm(f => ({ ...f, max_tokens: Number(e.target.value) }))} className={inp + " w-full"} />
        </label>
      </div>

      {msg && <div className="text-xs text-slate-300">{msg}</div>}
      <div className="flex gap-2 pt-2 border-t border-slate-800">
        <button onClick={reset} disabled={busy}
                className="text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 px-3 py-1.5 rounded inline-flex items-center gap-1">
          <RotateCcw size={11} /> Восстановить из кода
        </button>
        <button onClick={save} disabled={busy}
                className="ml-auto text-xs bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white font-bold px-3 py-1.5 rounded">
          {busy ? 'Сохраняю…' : 'Сохранить'}
        </button>
      </div>
    </div>
  );
}

// ── Trash editor ─────────────────────────────────────────────────────────────
function TrashEditor() {
  const [items, setItems] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setItems(await orgsApi.trash()); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const restore = async (o: Organization) => {
    await orgsApi.restore(o.id); load();
  };
  const purge = async (o: Organization) => {
    if (!window.confirm(`УДАЛИТЬ навсегда «${o.name}»? Действие необратимо: пропадут все инсталляции, тикеты и документы этой организации.`)) return;
    await orgsApi.purge(o.id); load();
  };

  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl">
      <header className="px-5 py-3 border-b border-slate-800">
        <h2 className="font-bold text-white">Корзина организаций</h2>
        <p className="text-[11px] text-slate-500 mt-1">
          Удалённые организации остаются здесь — их можно восстановить
          или удалить навсегда (с каскадом на инсталляции и тикеты).
        </p>
      </header>
      <div className="p-3">
        {loading ? (
          <Loader2 className="animate-spin text-blue-400 mx-auto my-4" />
        ) : items.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-6">Корзина пуста</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[10px] text-slate-500 uppercase tracking-wider">
              <tr>
                <th className="text-left py-2 px-3">Название</th>
                <th className="text-left py-2 px-3">ИНН</th>
                <th className="text-right py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(o => (
                <tr key={o.id} className="border-t border-slate-800">
                  <td className="py-2 px-3 text-slate-200">{o.name}</td>
                  <td className="py-2 px-3 text-xs font-mono text-slate-400">{o.inn}</td>
                  <td className="py-2 px-3 text-right whitespace-nowrap">
                    <button onClick={() => restore(o)} title="Восстановить"
                            className="inline-flex items-center gap-1 text-xs bg-slate-800 hover:bg-emerald-900/40 hover:text-emerald-300 text-slate-300 px-2 py-1 rounded mr-1">
                      <RotateCcw size={11} /> Восстановить
                    </button>
                    <button onClick={() => purge(o)} title="Удалить навсегда"
                            className="inline-flex items-center gap-1 text-xs bg-slate-800 hover:bg-red-900/40 hover:text-red-300 text-slate-300 px-2 py-1 rounded">
                      <Trash2 size={11} /> Навсегда
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

// ── SLA editor ───────────────────────────────────────────────────────────────
function SLAEditor() {
  const [plans, setPlans] = useState<SLAPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<SLAPlan | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setPlans(await slaApi.list()); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (p: SLAPlan) => {
    if (p.is_default) { alert('Нельзя удалить дефолтный план'); return; }
    if (p.is_pilot_default) { alert('Нельзя удалить пилотный дефолтный план'); return; }
    if (!window.confirm(`Удалить план «${p.name}»? Организации, использующие его, будут переведены на дефолтный.`)) return;
    await api.delete(`/api/v1/sla-plans/${p.id}`);
    load();
  };

  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl">
      <header className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
        <h2 className="font-bold text-white">SLA-планы</h2>
        <button onClick={() => setShowAdd(true)}
          className="text-xs flex items-center gap-1 bg-blue-600 hover:bg-blue-500 text-white px-3 py-1.5 rounded">
          <Plus size={12} /> Создать план
        </button>
      </header>
      <div className="p-3">
        {loading ? (
          <Loader2 className="animate-spin text-blue-400 mx-auto my-4" />
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[10px] text-slate-500 uppercase tracking-wider">
              <tr>
                <th className="text-left py-2 px-2">Название</th>
                <th className="text-right py-2 px-2">FRT P1/P2/P3/P4</th>
                <th className="text-right py-2 px-2">TTR P1/P2/P3/P4</th>
                <th className="text-center py-2 px-2">24×7</th>
                <th className="text-right py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {plans.map(p => (
                <tr key={p.id} className="border-t border-slate-800">
                  <td className="py-2 px-2 text-white">
                    {p.name}
                    {p.is_default && <span className="ml-2 text-[10px] text-emerald-400 uppercase">default</span>}
                    {p.is_pilot_default && <span className="ml-2 text-[10px] text-sky-300 uppercase">pilot default</span>}
                  </td>
                  <td className="py-2 px-2 text-right text-xs font-mono text-slate-300">
                    {fmtMinutes(p.frt_p1_min)} / {fmtMinutes(p.frt_p2_min)} / {fmtMinutes(p.frt_p3_min)} / {fmtMinutes(p.frt_p4_min)}
                  </td>
                  <td className="py-2 px-2 text-right text-xs font-mono text-slate-300">
                    {fmtMinutes(p.ttr_p1_min)} / {fmtMinutes(p.ttr_p2_min)} / {fmtMinutes(p.ttr_p3_min)} / {fmtMinutes(p.ttr_p4_min)}
                  </td>
                  <td className="py-2 px-2 text-center text-xs">{p.working_hours_24x7 ? '✓' : '—'}</td>
                  <td className="py-2 px-2 text-right">
                    <button onClick={() => setEditing(p)} className="text-slate-500 hover:text-blue-400 p-1"><Pencil size={12} /></button>
                    <button onClick={() => remove(p)} className="text-slate-500 hover:text-red-400 p-1"
                            disabled={p.is_default || p.is_pilot_default}><Trash2 size={12} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {editing && <SLAEditModal plan={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {showAdd && <SLAEditModal plan={null} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </section>
  );
}

function SLAEditModal({ plan, onClose, onSaved }:
  { plan: SLAPlan | null; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    name: plan?.name || '', description: plan?.description || '',
    is_default: plan?.is_default || false,
    is_pilot_default: plan?.is_pilot_default || false,
    frt_p1_min: plan?.frt_p1_min ?? 15, frt_p2_min: plan?.frt_p2_min ?? 60,
    frt_p3_min: plan?.frt_p3_min ?? 240, frt_p4_min: plan?.frt_p4_min ?? 1440,
    ttr_p1_min: plan?.ttr_p1_min ?? 240, ttr_p2_min: plan?.ttr_p2_min ?? 480,
    ttr_p3_min: plan?.ttr_p3_min ?? 960, ttr_p4_min: plan?.ttr_p4_min ?? 2400,
    working_hours_24x7: plan?.working_hours_24x7 ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const num = (k: keyof typeof form, v: string) => setForm(f => ({ ...f, [k]: Number(v) || 0 }));
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      if (plan) await api.patch(`/api/v1/sla-plans/${plan.id}`, form);
      else await api.post('/api/v1/sla-plans', form);
      onSaved();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setBusy(false); }
  };
  const inp = "w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm text-white focus:border-blue-500 outline-none";
  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 overflow-y-auto py-10" onClick={onClose}>
      <form onSubmit={submit}
            className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-xl mx-4 shadow-2xl"
            onClick={e => e.stopPropagation()}>
        <header className="p-4 border-b border-slate-800 flex justify-between">
          <h3 className="text-lg font-bold text-white">{plan ? 'Редактирование' : 'Новый'} SLA-плана</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white">✕</button>
        </header>
        <div className="p-4 space-y-3">
          <label className="block">
            <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Название *</span>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                   required className={inp} />
          </label>
          <label className="block">
            <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Описание</span>
            <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className={inp} />
          </label>
          <p className="text-[10px] text-slate-600">Все сроки в минутах. Например 60 = 1 ч, 1440 = 24 ч.</p>
          <table className="w-full text-xs">
            <thead className="text-slate-500"><tr><th></th><th>P1</th><th>P2</th><th>P3</th><th>P4</th></tr></thead>
            <tbody>
              <tr>
                <td className="text-slate-400 pr-2">FRT</td>
                <td><input type="number" min="0" value={form.frt_p1_min} onChange={e => num('frt_p1_min', e.target.value)} className={inp} /></td>
                <td><input type="number" min="0" value={form.frt_p2_min} onChange={e => num('frt_p2_min', e.target.value)} className={inp} /></td>
                <td><input type="number" min="0" value={form.frt_p3_min} onChange={e => num('frt_p3_min', e.target.value)} className={inp} /></td>
                <td><input type="number" min="0" value={form.frt_p4_min} onChange={e => num('frt_p4_min', e.target.value)} className={inp} /></td>
              </tr>
              <tr>
                <td className="text-slate-400 pr-2">TTR</td>
                <td><input type="number" min="0" value={form.ttr_p1_min} onChange={e => num('ttr_p1_min', e.target.value)} className={inp} /></td>
                <td><input type="number" min="0" value={form.ttr_p2_min} onChange={e => num('ttr_p2_min', e.target.value)} className={inp} /></td>
                <td><input type="number" min="0" value={form.ttr_p3_min} onChange={e => num('ttr_p3_min', e.target.value)} className={inp} /></td>
                <td><input type="number" min="0" value={form.ttr_p4_min} onChange={e => num('ttr_p4_min', e.target.value)} className={inp} /></td>
              </tr>
            </tbody>
          </table>
          <div className="flex flex-wrap gap-4 text-sm text-slate-300">
            <label className="flex items-center gap-2 cursor-pointer" title="Применяется ко всем тикетам, если у организации не выбран план явно">
              <input type="checkbox" checked={form.is_default}
                     onChange={e => setForm(f => ({ ...f, is_default: e.target.checked, ...(e.target.checked ? { is_pilot_default: false } : {}) }))} className="accent-blue-500" />
              По умолчанию
            </label>
            <label className="flex items-center gap-2 cursor-pointer" title="Применяется к тикетам пилотных организаций, если у них не выбран план явно">
              <input type="checkbox" checked={form.is_pilot_default}
                     onChange={e => setForm(f => ({ ...f, is_pilot_default: e.target.checked, ...(e.target.checked ? { is_default: false } : {}) }))} className="accent-blue-500" />
              Для пилотов
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.working_hours_24x7}
                     onChange={e => setForm(f => ({ ...f, working_hours_24x7: e.target.checked }))} className="accent-blue-500" />
              24×7
            </label>
          </div>
          <p className="text-[10px] text-slate-500">
            Приоритет резолва: explicit SLA организации → «Для пилотов» (если организация-пилот) → «По умолчанию».
          </p>
          {err && <div className="bg-red-900/30 border border-red-800 text-red-300 px-3 py-2 rounded text-sm">{err}</div>}
        </div>
        <div className="p-4 flex justify-end gap-2 border-t border-slate-800">
          <button type="button" onClick={onClose} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded text-slate-300 text-sm">Отмена</button>
          <button type="submit" disabled={busy || !form.name}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 rounded text-white text-sm font-bold">
            {busy ? 'Сохраняю…' : 'Сохранить'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Редактор одного справочника ──────────────────────────────────────────────
function DictionaryEditor({ dictKey }: { dictKey: string }) {
  const [items, setItems] = useState<OptionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  const isLicense = dictKey === 'license_option';

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const list = await optionsApi.list(dictKey);
      setItems(list);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [dictKey]);

  const move = async (id: string, dir: -1 | 1) => {
    const idx = items.findIndex(i => i.id === id);
    const next = idx + dir;
    if (idx < 0 || next < 0 || next >= items.length) return;
    const reordered = [...items];
    [reordered[idx], reordered[next]] = [reordered[next], reordered[idx]];
    // Перенумеровываем sort_order сплошняком и шлём bulk reorder.
    const payload = reordered.map((it, i) => ({ id: it.id, sort_order: i }));
    setItems(reordered.map((it, i) => ({ ...it, sort_order: i })));
    try {
      await optionsApi.reorder(dictKey, payload);
      invalidateOptions(dictKey);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
      await load();
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Удалить запись справочника?')) return;
    try {
      await optionsApi.remove(id);
      invalidateOptions(dictKey);
      await load();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    }
  };

  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl">
      <header className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
        <h2 className="font-bold text-white">{DICT_LABEL[dictKey] || dictKey}</h2>
        <button onClick={() => { setShowAdd(true); setEditingId(null); }}
          className="text-xs flex items-center gap-1 bg-blue-600 hover:bg-blue-500 text-white px-3 py-1.5 rounded">
          <Plus size={12} /> Добавить
        </button>
      </header>

      <div className="p-3">
        {err && <div className="bg-red-900/30 border border-red-800 text-red-300 px-3 py-2 rounded text-sm mb-3">{err}</div>}
        {loading ? (
          <Loader2 className="animate-spin text-blue-400 mx-auto my-4" />
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[10px] text-slate-500 uppercase tracking-wider">
              <tr>
                <th className="text-left py-2 px-3 w-8"></th>
                <th className="text-left py-2 px-3">Название</th>
                <th className="text-left py-2 px-3">Slug (value)</th>
                {isLicense && <th className="text-left py-2 px-3">Тип значения</th>}
                <th className="text-left py-2 px-3">Подсказка</th>
                <th className="text-right py-2 px-3 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {showAdd && (
                <RowEditor
                  dictKey={dictKey} isLicense={isLicense}
                  onCancel={() => setShowAdd(false)}
                  onSaved={async () => { setShowAdd(false); invalidateOptions(dictKey); await load(); }}
                />
              )}
              {items.length === 0 && !showAdd && (
                <tr><td colSpan={isLicense ? 6 : 5} className="text-center text-slate-500 py-6">
                  Справочник пуст. Нажми «Добавить».
                </td></tr>
              )}
              {items.map((it, i) => editingId === it.id ? (
                <RowEditor
                  key={it.id} dictKey={dictKey} isLicense={isLicense} initial={it}
                  onCancel={() => setEditingId(null)}
                  onSaved={async () => { setEditingId(null); invalidateOptions(dictKey); await load(); }}
                />
              ) : (
                <tr key={it.id} className="border-t border-slate-800 hover:bg-slate-800/40">
                  <td className="py-2 px-3 text-slate-600 font-mono text-xs">{i + 1}</td>
                  <td className="py-2 px-3 text-white">{it.label}</td>
                  <td className="py-2 px-3 text-slate-400 font-mono text-xs">{it.value}</td>
                  {isLicense && (
                    <td className="py-2 px-3 text-slate-300 text-xs">
                      {it.value_type === 'number' ? 'число (квота)' :
                       it.value_type === 'bool' ? 'булево (да/нет)' : '—'}
                    </td>
                  )}
                  <td className="py-2 px-3 text-slate-500 text-xs">{it.hint || ''}</td>
                  <td className="py-2 px-3 text-right">
                    <button onClick={() => move(it.id, -1)} disabled={i === 0}
                            className="text-slate-500 hover:text-blue-400 disabled:opacity-30 disabled:cursor-not-allowed p-1" title="Выше">
                      <ArrowUp size={12} />
                    </button>
                    <button onClick={() => move(it.id, 1)} disabled={i === items.length - 1}
                            className="text-slate-500 hover:text-blue-400 disabled:opacity-30 disabled:cursor-not-allowed p-1" title="Ниже">
                      <ArrowDown size={12} />
                    </button>
                    <button onClick={() => setEditingId(it.id)}
                            className="text-slate-500 hover:text-blue-400 p-1 ml-1" title="Редактировать">
                      <Pencil size={12} />
                    </button>
                    <button onClick={() => remove(it.id)}
                            className="text-slate-500 hover:text-red-400 p-1" title="Удалить">
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

// Строка-редактор: может работать как create (без initial) или как edit.
function RowEditor({
  dictKey, isLicense, initial, onCancel, onSaved,
}: {
  dictKey: string;
  isLicense: boolean;
  initial?: OptionItem;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [label, setLabel] = useState(initial?.label || '');
  const [value, setValue] = useState(initial?.value || '');
  const [hint, setHint] = useState(initial?.hint || '');
  const [valueType, setValueType] = useState<ValueType>(
    initial?.value_type ?? (isLicense ? 'bool' : null)
  );
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    if (!label.trim() || !value.trim()) {
      setErr('Заполни «Название» и «Slug».');
      return;
    }
    setSubmitting(true); setErr(null);
    try {
      if (initial) {
        await optionsApi.update(initial.id, {
          label: label.trim(), value: value.trim(), hint: hint || null,
          value_type: isLicense ? valueType : null,
        });
      } else {
        const payload: OptionItemIn = {
          dict_key: dictKey, label: label.trim(), value: value.trim(), hint: hint || null,
          value_type: isLicense ? valueType : null,
        };
        await optionsApi.create(payload);
      }
      onSaved();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setSubmitting(false); }
  };

  return (
    <tr className="border-t border-slate-800 bg-slate-800/30">
      <td className="py-2 px-3"></td>
      <td className="py-2 px-3">
        <input autoFocus value={label} onChange={e => setLabel(e.target.value)}
          placeholder="Например, «Astra Linux SE»"
          className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm text-white focus:border-blue-500 outline-none" />
      </td>
      <td className="py-2 px-3">
        <input value={value} onChange={e => setValue(e.target.value.replace(/[^a-z0-9_-]/g, ''))}
          placeholder="astra_se"
          className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm text-slate-300 font-mono focus:border-blue-500 outline-none" />
      </td>
      {isLicense && (
        <td className="py-2 px-3">
          <select value={valueType ?? ''} onChange={e => setValueType((e.target.value || null) as ValueType)}
            className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm text-white focus:border-blue-500 outline-none">
            <option value="bool">булево</option>
            <option value="number">число</option>
          </select>
        </td>
      )}
      <td className="py-2 px-3">
        <input value={hint} onChange={e => setHint(e.target.value)}
          placeholder="опционально"
          className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm text-slate-400 focus:border-blue-500 outline-none" />
      </td>
      <td className="py-2 px-3 text-right whitespace-nowrap">
        {err && <div className="text-[10px] text-red-400 mb-1">{err}</div>}
        <button onClick={submit} disabled={submitting}
                className="text-green-400 hover:text-green-300 p-1" title="Сохранить">
          {submitting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
        </button>
        <button onClick={onCancel} disabled={submitting}
                className="text-slate-500 hover:text-red-400 p-1" title="Отмена">
          <X size={14} />
        </button>
      </td>
    </tr>
  );
}


// ── Integrations: токены и интеграции ────────────────────────────────────────
// Группы интеграций. Порядок ключей внутри группы — порядок отображения.
// `key` соответствует app_settings.key (regex prefix-match достаточно для большинства).
const INTEGRATION_GROUPS: { id: string; label: string; prefixes: string[]; description?: string }[] = [
  { id: 'bots', label: 'Боты (MAX, Telegram)', prefixes: ['max_bot', 'telegram_bot'] },
  { id: 'ai', label: 'AI / RAG / автопилот', prefixes: ['deepseek', 'rag_', 'ai_autopilot'] },
  { id: 'email', label: 'Email / SMTP', prefixes: ['smtp_', 'email_'] },
  { id: 'redmine', label: 'Redmine эскалация', prefixes: ['redmine_'] },
  { id: 'crm', label: 'CRM (Bitrix24) + ФНС', prefixes: ['bitrix24_', 'fns_'] },
  { id: 'kb', label: 'База знаний (KB sync)',
    prefixes: ['delivery_repo', 'confluence_'] },
  { id: 's3', label: 'S3 (вложения + бэкап)', prefixes: ['s3_', 'backup_'] },
  { id: 'auth', label: 'SSO / LDAP / Keycloak', prefixes: ['keycloak_', 'ldap_'] },
];


function IntegrationsEditor() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [items, setItems] = useState<AppSettingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [activeGroup, setActiveGroup] = useState<string>(() =>
    localStorage.getItem('sd.integrationsGroup') || INTEGRATION_GROUPS[0].id);

  const load = async () => {
    setLoading(true); setErr(null);
    try { setItems(await appSettingsApi.list()); }
    catch (e: any) { setErr(e?.response?.data?.detail || e?.message || 'Ошибка'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (!isAdmin) {
    return (
      <section className="bg-slate-900 border border-slate-800 rounded-xl p-5 text-sm text-slate-400">
        Раздел доступен только администраторам.
      </section>
    );
  }

  const switchGroup = (g: string) => {
    setActiveGroup(g);
    localStorage.setItem('sd.integrationsGroup', g);
  };

  const groupItems = (groupId: string) => {
    const g = INTEGRATION_GROUPS.find(x => x.id === groupId);
    if (!g) return [];
    return items.filter(it => g.prefixes.some(p => it.key.startsWith(p)));
  };

  // Все ключи, попавшие в какую-то группу — для подсчёта «прочих».
  const known = new Set(items
    .filter(it => INTEGRATION_GROUPS.some(g => g.prefixes.some(p => it.key.startsWith(p))))
    .map(it => it.key));
  const otherItems = items.filter(it => !known.has(it.key));
  const groups = otherItems.length > 0
    ? [...INTEGRATION_GROUPS, { id: 'other', label: `Прочее (${otherItems.length})`, prefixes: [] }]
    : INTEGRATION_GROUPS;
  const visibleItems = activeGroup === 'other' ? otherItems : groupItems(activeGroup);

  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl">
      <header className="px-5 py-3 border-b border-slate-800">
        <h2 className="font-bold text-white flex items-center gap-2">
          <Key size={16} className="text-blue-400" /> Интеграции и токены
        </h2>
        <p className="text-[12px] text-slate-500 mt-1">
          Значения хранятся в БД и переопределяют то, что задано в
          <code> /etc/sd/sd.env</code>. После смены — клиенты пересоздаются на
          следующем вызове, рестарт сервиса не нужен.
        </p>
      </header>
      <nav className="flex flex-wrap gap-1 px-3 py-2 border-b border-slate-800 bg-slate-950/30">
        {groups.map(g => {
          const count = g.id === 'other' ? otherItems.length : groupItems(g.id).length;
          return (
            <button key={g.id} onClick={() => switchGroup(g.id)}
              className={`px-3 py-1 rounded text-xs font-bold uppercase transition ${
                activeGroup === g.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}>
              {g.label} {count > 0 && <span className="text-[10px] opacity-70">({count})</span>}
            </button>
          );
        })}
      </nav>
      <div className="p-4 space-y-3">
        {loading ? <Loader2 className="animate-spin text-blue-400 mx-auto my-4" /> :
         err ? <div className="bg-red-900/30 border border-red-800 text-red-300 px-3 py-2 rounded text-sm">{err}</div> :
         visibleItems.length === 0 ? (
           <div className="text-sm text-slate-500 text-center py-4">В этой группе нет настроек.</div>
         ) :
         visibleItems.map(it => (
           <div key={it.key}>
             <SettingRow item={it} onSaved={load} />
             {it.key === 'smtp_host' && <SmtpTestPanel />}
             {it.key === 'delivery_repo_url' && <RepoSyncPanel />}
           </div>
         ))}
      </div>
    </section>
  );
}

function RepoSyncPanel() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<any>(null);
  const run = async () => {
    setBusy(true); setResult(null);
    try { setResult(await appSettingsApi.repoSync()); }
    catch (e: any) {
      setResult({ ok: false, error: e?.response?.data?.detail || e?.message || 'Ошибка' });
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-1 ml-3 pl-3 border-l-2 border-slate-800 bg-slate-950/30 rounded-r p-2.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] text-slate-400">
          Авто-синк каждые 30 мин. Можно запустить вручную:
        </span>
        <button onClick={run} disabled={busy}
          className="text-xs inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white px-3 py-1 rounded">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
          Синхронизировать сейчас
        </button>
      </div>
      {result && (
        <div className={`mt-2 text-[11px] px-2 py-1.5 rounded ${
          result.ok ? 'bg-emerald-900/30 border border-emerald-800 text-emerald-200'
                    : 'bg-red-900/30 border border-red-800 text-red-200'
        }`}>
          {result.skipped ? (
            <>⊝ Пропущено: {result.reason}</>
          ) : result.ok ? (
            <>
              ✓ Синк выполнен · ревизия <code className="text-blue-300">{result.revision || '?'}</code>{' '}
              · {result.cloned ? 'клонирован' : 'обновлён'}
              <span className="ml-2">
                +{result.added} новых, ~{result.updated} обновлено,{' '}
                ={result.skipped_files} без изменений
              </span>
            </>
          ) : (
            <>✗ Ошибка на шаге <code>{result.step || '?'}</code>: {result.error}</>
          )}
        </div>
      )}
    </div>
  );
}

function SmtpTestPanel() {
  const { user } = useAuth();
  const [to, setTo] = useState(user?.email || 'kzhebenev@gmail.com');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);
  const run = async () => {
    setBusy(true); setResult(null);
    try {
      const r = await appSettingsApi.testSmtp(to);
      setResult(r);
    } catch (e: any) {
      setResult({ ok: false, message: e?.response?.data?.detail || e?.message || 'Ошибка' });
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-1 ml-3 pl-3 border-l-2 border-slate-800 bg-slate-950/30 rounded-r p-2.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] text-slate-400">Проверка SMTP: тест-письмо на</span>
        <input value={to} onChange={e => setTo(e.target.value)}
          type="email" placeholder="email@example.com"
          className="flex-1 min-w-[200px] bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-white focus:border-blue-500 outline-none" />
        <button onClick={run} disabled={busy || !to.trim()}
          className="text-xs inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white px-3 py-1 rounded">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
          Отправить тест
        </button>
      </div>
      {result && (
        <div className={`mt-2 text-[11px] px-2 py-1 rounded ${
          result.ok ? 'bg-emerald-900/30 border border-emerald-800 text-emerald-200'
                   : 'bg-red-900/30 border border-red-800 text-red-200'
        }`}>
          {result.ok ? '✓ ' : '✗ '}{result.message}
        </div>
      )}
    </div>
  );
}

function SettingRow({ item, onSaved }: { item: AppSettingItem; onSaved: () => void }) {
  const [value, setValue] = useState('');
  const [edit, setEdit] = useState(!item.is_secret);  // несекреты редактируем сразу
  const [reveal, setReveal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // несекреты подтягиваем как есть
  useEffect(() => {
    if (!item.is_secret) setValue(item.value);
  }, [item.is_secret, item.value]);

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      await appSettingsApi.update(item.key, value);
      setSavedAt(Date.now());
      if (item.is_secret) { setValue(''); setEdit(false); }
      onSaved();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setSaving(false); }
  };

  const clear = async () => {
    if (!window.confirm(`Очистить значение «${item.key}»?`)) return;
    setSaving(true); setErr(null);
    try { await appSettingsApi.update(item.key, ''); setValue(''); setEdit(false); onSaved(); }
    catch (e: any) { setErr(e?.response?.data?.detail || e?.message || 'Ошибка'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-slate-950/40 border border-slate-800 rounded-lg p-3">
      <div className="flex items-center justify-between mb-1.5">
        <div>
          <code className="text-[11px] font-mono text-blue-300">{item.key}</code>
          {item.has_value && <span className="ml-2 text-[10px] text-emerald-400">● настроено</span>}
          {!item.has_value && <span className="ml-2 text-[10px] text-amber-400">○ пусто</span>}
        </div>
        {savedAt && Date.now() - savedAt < 3000 && (
          <span className="text-[11px] text-emerald-400 flex items-center gap-1">
            <Check size={12} /> сохранено
          </span>
        )}
      </div>
      {item.description && (
        <div className="text-[12px] text-slate-400 mb-2">{item.description}</div>
      )}
      <div className="flex items-center gap-2">
        {item.is_secret && !edit ? (
          <>
            <code className="flex-1 bg-slate-950 border border-slate-800 rounded px-2 py-1.5 text-xs font-mono text-slate-400 truncate">
              {item.masked || '(не задан)'}
            </code>
            <button onClick={() => { setEdit(true); setValue(''); }}
              className="text-xs px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded">
              Изменить
            </button>
            {item.has_value && (
              <button onClick={clear} disabled={saving}
                className="text-xs px-3 py-1.5 bg-red-900/40 hover:bg-red-900 text-red-300 rounded">
                Очистить
              </button>
            )}
          </>
        ) : (
          <>
            <input
              type={item.is_secret && !reveal ? 'password' : 'text'}
              value={value}
              onChange={e => setValue(e.target.value)}
              placeholder={item.is_secret ? 'новый токен…' : ''}
              autoComplete="off"
              className="flex-1 bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-sm text-white font-mono focus:border-blue-500 outline-none"
            />
            {item.is_secret && (
              <button type="button" onClick={() => setReveal(r => !r)}
                className="text-slate-400 hover:text-white p-1" title={reveal ? 'Скрыть' : 'Показать'}>
                {reveal ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            )}
            <button onClick={save} disabled={saving || (!value && item.is_secret)}
              className="text-xs px-3 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white rounded flex items-center gap-1">
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Сохранить
            </button>
            {item.is_secret && (
              <button onClick={() => { setEdit(false); setValue(''); setErr(null); }}
                className="text-xs px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded">
                Отмена
              </button>
            )}
          </>
        )}
      </div>
      {err && <div className="text-[11px] text-red-400 mt-1.5">{err}</div>}
    </div>
  );
}


// ── Reply templates editor ──────────────────────────────────────────────────
function ReplyTemplatesEditor() {
  const [items, setItems] = useState<ReplyTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<ReplyTemplate | 'new' | null>(null);

  const load = async () => {
    setLoading(true);
    try { setItems(await replyTemplatesApi.list()); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onDelete = async (t: ReplyTemplate) => {
    if (!window.confirm(`Удалить шаблон «${t.title}»?`)) return;
    await replyTemplatesApi.remove(t.id);
    load();
  };

  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl">
      <header className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
        <h2 className="font-bold text-white flex items-center gap-2">
          <FileText size={16} className="text-blue-400" /> Шаблоны ответов
        </h2>
        <button onClick={() => setEditing('new')}
          className="text-xs flex items-center gap-1 bg-blue-600 hover:bg-blue-500 text-white px-3 py-1.5 rounded">
          <Plus size={12} /> Новый шаблон
        </button>
      </header>
      <div className="p-3">
        <p className="text-[11px] text-slate-500 mb-2">
          Подставляются в форму ответа в карточке тикета через
          «Шаблон ответа». Можно сделать личным (виден только вам)
          или общим (доступен всему staff).
        </p>
        {loading ? <Loader2 className="animate-spin text-blue-400 mx-auto my-4" /> :
         items.length === 0 ? <div className="text-sm text-slate-500 text-center py-6">Шаблонов ещё нет</div> :
         <ul className="divide-y divide-slate-800">
           {items.map(t => (
             <li key={t.id} className="py-2 px-2 flex items-start gap-2">
               <div className="flex-1 min-w-0">
                 <div className="text-sm text-white font-medium truncate">
                   {t.title}
                   <span className={`ml-2 text-[10px] px-1 py-0.5 rounded ${
                     t.is_shared ? 'bg-emerald-900/30 text-emerald-300'
                                 : 'bg-yellow-900/30 text-yellow-300'}`}>
                     {t.is_shared ? 'общий' : 'личный'}
                   </span>
                 </div>
                 <div className="text-[11px] text-slate-500 line-clamp-2 mt-0.5">{t.body}</div>
               </div>
               <button onClick={() => setEditing(t)} title="Редактировать"
                 className="p-1 text-slate-500 hover:text-blue-400">
                 <Pencil size={12} />
               </button>
               <button onClick={() => onDelete(t)} title="Удалить"
                 className="p-1 text-slate-500 hover:text-red-400">
                 <Trash2 size={12} />
               </button>
             </li>
           ))}
         </ul>}
      </div>
      {editing && (
        <ReplyTemplateModal initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
    </section>
  );
}

function ReplyTemplateModal({ initial, onClose, onSaved }:
  { initial: ReplyTemplate | null; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    title: initial?.title || '',
    body: initial?.body || '',
    is_shared: initial?.is_shared ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try {
      if (initial) await replyTemplatesApi.update(initial.id, form);
      else await replyTemplatesApi.create(form);
      onSaved();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 overflow-y-auto py-10" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-xl mx-4 shadow-2xl"
           onClick={e => e.stopPropagation()}>
        <header className="p-4 border-b border-slate-800 flex justify-between">
          <h3 className="text-lg font-bold text-white">{initial ? 'Редактировать шаблон' : 'Новый шаблон ответа'}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white">✕</button>
        </header>
        <form onSubmit={submit} className="p-4 space-y-3">
          <label className="block">
            <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Название</span>
            <input required value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none" />
          </label>
          <label className="block">
            <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Текст ответа</span>
            <textarea value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} rows={10}
              className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white font-sans focus:border-blue-500 outline-none" />
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-300">
            <input type="checkbox" checked={form.is_shared}
                   onChange={e => setForm(f => ({ ...f, is_shared: e.target.checked }))} />
            Общий шаблон (доступен всему staff)
          </label>
          {err && <div className="bg-red-900/30 border border-red-800 text-red-300 px-3 py-2 rounded text-sm">{err}</div>}
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-800 -mx-4 px-4 -mb-4 pb-4 mt-3">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded text-slate-300 text-sm">Отмена</button>
            <button type="submit" disabled={busy || !form.title.trim()}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 rounded text-white text-sm font-bold inline-flex items-center gap-1.5">
              <Save size={13} /> {busy ? 'Сохраняю…' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ── Templates editor (bot или email) ────────────────────────────────────────
function TemplatesEditor({ category }: { category: 'bot' | 'email' }) {
  const [items, setItems] = useState<TemplateRegistryItem[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const all = await appSettingsApi.templates();
      setItems(all.filter(t => t.category === category));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [category]);

  const isBot = category === 'bot';
  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl">
      <header className="px-5 py-3 border-b border-slate-800">
        <h2 className="font-bold text-white flex items-center gap-2">
          {isBot ? <MessageSquare size={16} className="text-blue-400" />
                 : <Mail size={16} className="text-amber-400" />}
          {isBot ? 'Шаблоны сообщений бота' : 'Шаблоны писем электронной почты'}
        </h2>
        <p className="text-[11px] text-slate-500 mt-1">
          {isBot
            ? 'Тексты ответов MAX/TG-ботов. Можно редактировать; если поле очищено — используется встроенный текст.'
            : 'HTML-шаблоны исходящих писем (активация, сброс пароля, уведомления о тикетах и SLA). Пустое поле = встроенный текст.'}
        </p>
      </header>
      <div className="p-3 space-y-3">
        {loading ? <Loader2 className="animate-spin text-blue-400 mx-auto my-4" /> :
         items.map(t => <TemplateRow key={t.key} item={t} onSaved={load} />)}
      </div>
    </section>
  );
}

function TemplateRow({ item, onSaved }:
  { item: TemplateRegistryItem; onSaved: () => void }) {
  // Если в БД значение пустое — подгружаем встроенный дефолт прямо в textarea,
  // чтобы пользователь видел текст и мог редактировать. При сохранении мы
  // сравним результат с дефолтом и, если совпадает, отправим '' (т.е. оставим
  // fallback на код — это позволит видеть будущие изменения дефолта).
  const initial = item.value || item.default;
  const [value, setValue] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [showDefault, setShowDefault] = useState(false);

  useEffect(() => {
    setValue(item.value || item.default);
  }, [item.value, item.default, item.key]);

  const save = async () => {
    // Текст идентичен дефолту → пишем пустоту (используем встроенный fallback).
    const toSave = value === item.default ? '' : value;
    setBusy(true);
    try { await appSettingsApi.update(item.key, toSave); setSavedAt(Date.now()); onSaved(); }
    finally { setBusy(false); }
  };
  const loadDefault = () => {
    setValue(item.default);
  };
  const resetToBuiltin = async () => {
    if (!window.confirm('Очистить кастомный шаблон и вернуться к встроенному дефолту?')) return;
    setBusy(true);
    try {
      await appSettingsApi.update(item.key, '');
      setValue(item.default);
      setSavedAt(Date.now());
      onSaved();
    } finally { setBusy(false); }
  };

  // Логика бейджей: значение в БД задано (item.value != '') и отличается от дефолта.
  const isCustom = !!item.value && item.value !== item.default;
  const isUsingDefault = !item.value;

  return (
    <div className="bg-slate-950/40 border border-slate-800 rounded-lg p-3">
      <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <code className="text-[11px] font-mono text-blue-300">{item.key}</code>
          {isUsingDefault && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400"
                  title="В БД пусто — используется встроенный текст из кода">
              используется встроенный
            </span>
          )}
          {isCustom && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-900/30 text-emerald-300"
                  title="В БД сохранена своя версия — отличается от встроенного">
              кастомный
            </span>
          )}
        </div>
        {savedAt && Date.now() - savedAt < 3000 && (
          <span className="text-[11px] text-emerald-400 flex items-center gap-1"><Check size={12} /> сохранено</span>
        )}
      </div>
      <div className="text-sm text-slate-200 mb-1">{item.label}</div>
      <div className="text-[11px] text-slate-500 mb-2">{item.description}</div>

      {item.placeholders.length > 0 && (
        <div className="text-[10px] text-slate-500 mb-2 flex flex-wrap items-center gap-1">
          <span className="uppercase tracking-wider mr-1">Плейсхолдеры:</span>
          {item.placeholders.map(p => (
            <code key={p} onClick={() => {
              navigator.clipboard.writeText(`{${p}}`);
            }} title="Скопировать"
              className="cursor-pointer bg-slate-800 text-blue-300 px-1.5 py-0.5 rounded font-mono hover:bg-slate-700">
              {`{${p}}`}
            </code>
          ))}
        </div>
      )}

      <textarea value={value} onChange={e => setValue(e.target.value)} rows={item.rows}
        className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-sm text-white font-mono focus:border-blue-500 outline-none" />

      <div className="flex flex-wrap justify-between gap-2 mt-2 items-center">
        <div className="flex gap-2 flex-wrap">
          {value !== item.default && (
            <button onClick={loadDefault} type="button"
              className="text-[11px] bg-slate-800 hover:bg-slate-700 text-slate-200 px-2.5 py-1 rounded inline-flex items-center gap-1">
              <RotateCcw size={11} /> Сбросить поле к дефолту
            </button>
          )}
          <button onClick={() => setShowDefault(v => !v)} type="button"
            className="text-[11px] bg-slate-800 hover:bg-slate-700 text-slate-200 px-2.5 py-1 rounded inline-flex items-center gap-1">
            {showDefault ? <EyeOff size={11} /> : <Eye size={11} />}
            {showDefault ? 'Скрыть' : 'Показать'} встроенный
          </button>
          {isCustom && (
            <button onClick={resetToBuiltin} type="button"
              className="text-[11px] bg-red-900/30 hover:bg-red-900/60 text-red-200 px-2.5 py-1 rounded">
              Очистить кастом (использовать встроенный)
            </button>
          )}
        </div>
        <button onClick={save} disabled={busy || value === (item.value || item.default)}
          className="text-xs bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white px-3 py-1.5 rounded inline-flex items-center gap-1">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Сохранить
        </button>
      </div>

      {showDefault && (
        <pre className="mt-2 bg-slate-950 border border-dashed border-slate-700 rounded p-2 text-[11px] text-slate-400 font-mono whitespace-pre-wrap break-all max-h-60 overflow-y-auto">
{item.default}
        </pre>
      )}
    </div>
  );
}


// ── Видимость организаций (массовое скрытие из списка) ───────────────────────
function OrgVisibilityEditor() {
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setSel(new Set());
    try {
      // включаем скрытые — для управления тут они нужны
      const { data } = await api.get<Organization[]>('/api/v1/organizations',
        { params: { show_hidden: true } });
      setOrgs(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = orgs.filter(o => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return o.name.toLowerCase().includes(s) || (o.inn || '').includes(s);
  });

  const toggle = (id: string) => setSel(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const toggleAll = () => {
    if (sel.size === filtered.length) setSel(new Set());
    else setSel(new Set(filtered.map(o => o.id)));
  };

  const apply = async (hide: boolean) => {
    if (sel.size === 0) return;
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post('/api/v1/organizations/_bulk-visibility',
        { ids: Array.from(sel), is_hidden: hide });
      setMsg(`Обновлено: ${data.updated}`);
      await load();
    } catch (e: any) {
      setMsg('Ошибка: ' + (e?.response?.data?.detail || e?.message));
    } finally { setBusy(false); }
  };

  const visibleN = orgs.filter(o => !o.is_hidden).length;
  const hiddenN = orgs.length - visibleN;

  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl">
      <header className="px-5 py-3 border-b border-slate-800 flex flex-wrap items-center gap-3">
        <h2 className="font-bold text-white">Видимость организаций</h2>
        <span className="text-xs text-slate-500">
          видимых: {visibleN} · скрытых: {hiddenN} · всего: {orgs.length}
        </span>
        <div className="ml-auto relative">
          <input value={q} onChange={e => setQ(e.target.value)}
            placeholder="Поиск по имени или ИНН…"
            className="bg-slate-950 border border-slate-700 rounded-full py-1.5 px-4 text-xs text-white w-72" />
        </div>
      </header>

      <div className="px-5 py-2 border-b border-slate-800 flex flex-wrap items-center gap-2">
        <button onClick={toggleAll}
          className="text-[11px] bg-slate-800 hover:bg-slate-700 text-slate-200 px-2.5 py-1 rounded">
          {sel.size === filtered.length && filtered.length > 0
            ? 'Снять выделение' : 'Выделить все видимые'}
        </button>
        <span className="text-[11px] text-slate-500">выделено: {sel.size}</span>
        <div className="ml-auto flex gap-2">
          <button onClick={() => apply(true)} disabled={!sel.size || busy}
            className="text-xs bg-yellow-600 hover:bg-yellow-500 disabled:bg-slate-700 text-white font-bold px-3 py-1.5 rounded inline-flex items-center gap-1.5">
            <EyeOff size={13} /> Скрыть
          </button>
          <button onClick={() => apply(false)} disabled={!sel.size || busy}
            className="text-xs bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 text-white font-bold px-3 py-1.5 rounded inline-flex items-center gap-1.5">
            <Eye size={13} /> Показать
          </button>
        </div>
      </div>

      {msg && <div className="px-5 py-2 text-xs text-slate-300">{msg}</div>}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="animate-spin text-blue-400" /></div>
      ) : (
        <ul className="max-h-[calc(100vh-280px)] overflow-y-auto divide-y divide-slate-800">
          {filtered.map(o => (
            <li key={o.id}
              className={`flex items-center gap-3 px-5 py-2 cursor-pointer hover:bg-slate-800/40 ${
                o.is_hidden ? 'opacity-60' : ''
              }`}
              onClick={() => toggle(o.id)}>
              <input type="checkbox" checked={sel.has(o.id)} readOnly
                className="accent-blue-500 pointer-events-none" />
              {o.is_hidden
                ? <EyeOff size={13} className="text-slate-600" />
                : <Eye size={13} className="text-emerald-500" />}
              <div className="flex-1 min-w-0">
                <a href={`/organizations/${o.id}`}
                   onClick={e => e.stopPropagation()}
                   className="text-sm text-white hover:text-blue-400 truncate block">
                  {o.name}
                </a>
                <div className="text-[10px] font-mono text-slate-500">
                  ИНН {o.inn || '—'}
                  {(o.tickets_open_count ?? 0) > 0 && (
                    <span className="ml-3 text-yellow-400">open: {o.tickets_open_count}</span>
                  )}
                  {(o.tickets_closed_count ?? 0) > 0 && (
                    <span className="ml-2 text-slate-600">closed: {o.tickets_closed_count}</span>
                  )}
                </div>
              </div>
            </li>
          ))}
          {filtered.length === 0 && (
            <li className="px-5 py-6 text-sm text-slate-500 text-center">Нет результатов</li>
          )}
        </ul>
      )}
    </section>
  );
}


// ── Дубликаты организаций (мерж) ────────────────────────────────────────────
interface DupGroup {
  reason: string;
  key: string;
  members: { id: string; name: string; inn: string;
             contact_email?: string|null; contact_phone?: string|null;
             tickets_open_count?: number; tickets_closed_count?: number;
             hde_id?: number|null; bc_customer_id?: number|null }[];
}

const REASON_LABEL: Record<string,string> = {
  name: 'одинаковое имя',
  inn:  'одинаковый ИНН',
  bc:   'один и тот же Battlecard.Customer',
};

function OrgDuplicatesEditor() {
  const [groups, setGroups] = useState<DupGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/v1/organizations/_duplicates');
      setGroups(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const merge = async (group: DupGroup, keepId: string) => {
    const merge_ids = group.members.filter(m => m.id !== keepId).map(m => m.id);
    const keep = group.members.find(m => m.id === keepId);
    if (!keep) return;
    if (!window.confirm(
      `Объединить ${merge_ids.length} организаций в «${keep.name}»?\n\n` +
      `Все тикеты, клиенты и инсталляции переедут в «${keep.name}». ` +
      `Лишние записи будут удалены безвозвратно.`
    )) return;
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post(`/api/v1/organizations/${keepId}/merge`,
        { merge_ids });
      setMsg(`OK: ${data.orgs_merged} организаций объединены. ` +
             `Тикеты: ${data.tickets_moved}, клиенты: ${data.users_moved}, ` +
             `инсталляции: ${data.installs_moved}.`);
      await load();
    } catch (e: any) {
      setMsg('Ошибка: ' + (e?.response?.data?.detail || e?.message));
    } finally { setBusy(false); }
  };

  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl">
      <header className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
        <h2 className="font-bold text-white">Дубликаты организаций</h2>
        <button onClick={load} className="text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 px-3 py-1.5 rounded">
          ↻ Пересканировать
        </button>
      </header>
      {msg && <div className="px-5 py-2 text-xs text-slate-300 border-b border-slate-800">{msg}</div>}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="animate-spin text-blue-400" /></div>
      ) : groups.length === 0 ? (
        <div className="px-5 py-10 text-sm text-slate-500 text-center">
          Кандидатов на дубли не найдено.
        </div>
      ) : (
        <ul className="divide-y divide-slate-800">
          {groups.map((g, i) => (
            <li key={i} className="px-5 py-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">
                {REASON_LABEL[g.reason] || g.reason} · <span className="font-mono">{g.key}</span>
              </div>
              <div className="space-y-1.5">
                {g.members.map(m => (
                  <div key={m.id} className="flex items-center gap-2 bg-slate-950/40 border border-slate-800 rounded px-3 py-1.5">
                    <a href={`/organizations/${m.id}`}
                       className="text-sm text-white hover:text-blue-400 truncate min-w-0 flex-1">
                      {m.name}
                    </a>
                    <span className="text-[10px] text-slate-500 font-mono">ИНН {m.inn}</span>
                    {(m.tickets_open_count ?? 0) > 0 && (
                      <span className="text-[10px] text-yellow-400">open {m.tickets_open_count}</span>
                    )}
                    {(m.tickets_closed_count ?? 0) > 0 && (
                      <span className="text-[10px] text-slate-500">closed {m.tickets_closed_count}</span>
                    )}
                    {m.hde_id && <span className="text-[10px] text-purple-400">HDE</span>}
                    {m.bc_customer_id && <span className="text-[10px] text-cyan-400">BC</span>}
                    <button onClick={() => merge(g, m.id)} disabled={busy}
                      className="text-[10px] bg-emerald-700 hover:bg-emerald-600 text-white px-2 py-0.5 rounded font-bold">
                      Оставить эту
                    </button>
                  </div>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Clients (CRUD + reset password + Organization column)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Достаёт человекочитаемое сообщение из axios-ошибки. FastAPI на 422
 * присылает detail как массив объектов {type, loc, msg, ...} — если
 * вернуть его прямо в React, получим Minified React error #31
 * (objects are not valid as a React child). Здесь же конвертируем
 * любую форму detail в строку.
 */
function extractErrorMessage(e: any): string {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d.map((it: any) => {
      const loc = Array.isArray(it?.loc) ? it.loc.filter((x: any) => typeof x === 'string').join('.') : '';
      const msg = typeof it?.msg === 'string' ? it.msg : JSON.stringify(it);
      return loc ? `${loc}: ${msg}` : msg;
    }).join('; ');
  }
  if (d && typeof d === 'object') return JSON.stringify(d);
  return e?.message || 'Ошибка';
}

function ClientsEditor() {
  const [items, setItems] = useState<ClientUser[]>([]);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');
  const [filterOrg, setFilterOrg] = useState<string>('');

  const orgById = new Map<string, Organization>(orgs.map(o => [o.id, o]));

  const load = async () => {
    setLoading(true);
    try {
      const [users, orgList] = await Promise.all([
        clientsApi.list(),
        orgsApi.list(),
      ]);
      setItems(users);
      setOrgs(orgList);
    } finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, []);

  const filtered = items.filter(u => {
    if (filterOrg && u.organization_id !== filterOrg) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    const orgName = u.organization_id ? (orgById.get(u.organization_id)?.name || '').toLowerCase() : '';
    return (u.email || '').toLowerCase().includes(q)
        || (u.full_name || '').toLowerCase().includes(q)
        || orgName.includes(q);
  });

  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl">
      <header className="flex items-center justify-between gap-3 px-5 py-3 border-b border-slate-800">
        <h2 className="font-bold text-white">Клиенты</h2>
        <div className="flex items-center gap-2">
          <input className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-white placeholder-slate-500"
                 placeholder="поиск по email / имени / организации"
                 value={search} onChange={e => setSearch(e.target.value)} />
          <select className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-white"
                  value={filterOrg} onChange={e => setFilterOrg(e.target.value)}>
            <option value="">все организации</option>
            {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          <button onClick={() => setShowAdd(true)}
            className="text-xs flex items-center gap-1 bg-blue-600 hover:bg-blue-500 text-white px-3 py-1.5 rounded">
            <Plus size={12} /> Создать
          </button>
        </div>
      </header>
      <div className="p-3">
        {loading ? (
          <Loader2 className="animate-spin text-blue-400 mx-auto my-4" />
        ) : filtered.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-6">
            {items.length === 0 ? 'Нет клиентов' : 'Ничего не найдено по фильтрам'}
          </div>
        ) : (
          (() => {
            // Группируем клиентов по организациям. Клиенты с org_id, для которого
            // нет записи в orgById (организация скрыта/удалена), складываются в
            // единую группу «Без организации».
            const groups = new Map<string | null, ClientUser[]>();
            for (const u of filtered) {
              const key = u.organization_id && orgById.has(u.organization_id) ? u.organization_id : null;
              if (!groups.has(key)) groups.set(key, []);
              groups.get(key)!.push(u);
            }
            // Сортировка групп: по имени организации; «без организации» — в конце.
            const orgKeys = [...groups.keys()].sort((a, b) => {
              if (a === null) return 1;
              if (b === null) return -1;
              const na = (orgById.get(a)?.name || '').toLowerCase();
              const nb = (orgById.get(b)?.name || '').toLowerCase();
              return na.localeCompare(nb, 'ru');
            });
            // Единая таблица для всех групп, чтобы ширины колонок совпадали
            // между блоками. Группа = <tr> с colspan-заголовком, далее строки клиентов.
            return (
              <table className="w-full text-sm table-fixed">
                <colgroup>
                  <col style={{ width: '28%' }} />
                  <col style={{ width: '42%' }} />
                  <col style={{ width: '30%' }} />
                </colgroup>
                <thead className="text-[10px] text-slate-500 uppercase tracking-wider sticky top-0 bg-slate-900">
                  <tr>
                    <th className="text-left py-2 px-3">Имя</th>
                    <th className="text-left py-2 px-3">Email · Телефон</th>
                    <th className="text-right py-2 px-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {orgKeys.map(orgId => {
                    const org = orgId ? orgById.get(orgId) || null : null;
                    const usersIn = groups.get(orgId)!;
                    return (
                      <>
                        <tr key={'_g_' + (orgId || 'no_org')} className="bg-slate-800/50">
                          <td colSpan={3} className="px-3 py-2">
                            <div className="flex items-center justify-between gap-2">
                              <div className="font-bold text-white text-sm">
                                {org ? (
                                  <a href={`/organizations/${org.id}`} className="text-blue-400 hover:text-blue-300">{org.name}</a>
                                ) : (
                                  <span className="text-slate-500">Без организации</span>
                                )}
                                <span className="ml-2 text-[10px] text-slate-500 font-mono">
                                  {usersIn.length} {usersIn.length === 1 ? 'клиент' : 'клиентов'}
                                </span>
                              </div>
                              {org?.inn && <span className="text-[10px] text-slate-500 font-mono">ИНН {org.inn}</span>}
                            </div>
                          </td>
                        </tr>
                        {usersIn.map(u => (
                          <ClientRow key={u.id} u={u} onChanged={load} hideOrgColumn orgs={orgs} />
                        ))}
                      </>
                    );
                  })}
                </tbody>
              </table>
            );
          })()
        )}
        <div className="mt-2 text-[10px] text-slate-500">показано {filtered.length} из {items.length}</div>
      </div>
      {showAdd && <ClientCreateModal orgs={orgs} onClose={() => setShowAdd(false)}
                                     onCreated={() => { setShowAdd(false); load(); }} />}
    </section>
  );
}

function ClientRow({ u, org, onChanged, hideOrgColumn, orgs }:
  { u: ClientUser; org?: Organization | null; onChanged: () => void; hideOrgColumn?: boolean; orgs?: Organization[] }) {
  const [resetUrl, setResetUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);

  const reset = async () => {
    if (!window.confirm(`Сгенерировать новую активационную ссылку для ${u.email}?`)) return;
    setBusy(true);
    try {
      const r = await clientsApi.resetPassword(u.id);
      setResetUrl(r.activation_url);
    } finally { setBusy(false); }
  };
  const toggle = async () => {
    setBusy(true);
    try { await clientsApi.patch(u.id, { is_active: !u.is_active }); onChanged(); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Удалить клиента ${u.email}? Это действие необратимо.`)) return;
    setBusy(true);
    try { await clientsApi.remove(u.id); onChanged(); }
    finally { setBusy(false); }
  };
  const copy = () => {
    if (!resetUrl) return;
    navigator.clipboard.writeText(resetUrl).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <>
      <tr className="border-t border-slate-800">
        <td className="py-2 px-3 text-slate-200">
          {u.full_name || '—'}
          {!u.is_active && <span className="ml-2 text-[10px] text-red-400">disabled</span>}
        </td>
        <td className="py-2 px-3 text-slate-400 font-mono text-[11px]">
          <div>{u.email}</div>
          {u.phone && <div className="text-slate-500">{u.phone}</div>}
        </td>
        {!hideOrgColumn && (
          <td className="py-2 px-3 text-xs">
            {org ? (
              <a href={`/organizations/${org.id}`} className="text-blue-400 hover:text-blue-300">{org.name}</a>
            ) : (
              <span className="text-slate-600">—</span>
            )}
          </td>
        )}
        <td className="py-2 px-3 text-right whitespace-nowrap">
          <button onClick={() => setEditing(true)} disabled={busy} title="Редактировать"
                  className="p-1 text-slate-500 hover:text-blue-400"><Pencil size={12} /></button>
          <button onClick={reset} disabled={busy} title="Сбросить пароль (новая активационная ссылка)"
                  className="p-1 text-slate-500 hover:text-blue-400"><RotateCcw size={12} /></button>
          <button onClick={toggle} disabled={busy} title={u.is_active ? 'Заблокировать' : 'Разблокировать'}
                  className="p-1 text-slate-500 hover:text-yellow-400">
            {u.is_active ? <Lock size={12} /> : <Unlock size={12} />}
          </button>
          <button onClick={remove} disabled={busy} title="Удалить"
                  className="p-1 text-slate-500 hover:text-red-400"><Trash2 size={12} /></button>
          {resetUrl && (
            <div className="mt-1 flex items-center gap-1 justify-end">
              <span className="text-[10px] font-mono text-blue-300 truncate max-w-[280px]">{resetUrl}</span>
              <button onClick={copy} className="text-slate-500 hover:text-blue-400 p-0.5">
                {copied ? <Check size={11} /> : <Copy size={11} />}
              </button>
            </div>
          )}
        </td>
      </tr>
      {editing && <ClientEditModal user={u} orgs={orgs || []} onClose={() => setEditing(false)}
                                    onSaved={() => { setEditing(false); onChanged(); }} />}
    </>
  );
}

function ClientCreateModal({ orgs, onClose, onCreated }:
  { orgs: Organization[]; onClose: () => void; onCreated: () => void }) {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [orgId, setOrgId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!email || !orgId) { setErr('email и организация обязательны'); return; }
    setBusy(true);
    try {
      const r = await clientsApi.create({ email, full_name: fullName, organization_id: orgId });
      setCreatedUrl(r.activation_url || null);
    } catch (e: any) {
      setErr(extractErrorMessage(e));
    } finally { setBusy(false); }
  };
  const finish = () => { onCreated(); };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl p-5 max-w-md w-full">
        <h3 className="font-bold text-white mb-3">Новый клиент</h3>
        {createdUrl ? (
          <div>
            <p className="text-sm text-slate-300 mb-2">
              Клиент создан. Активационная ссылка (отправьте её клиенту):
            </p>
            <textarea readOnly className="w-full bg-slate-800 border border-slate-700 rounded text-xs font-mono p-2 text-blue-300 h-20"
                      value={createdUrl} />
            <button onClick={finish}
                    className="mt-3 w-full bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold py-2 rounded">
              Готово
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="text-xs text-slate-400">Email *</label>
              <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                     className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-white" />
            </div>
            <div>
              <label className="text-xs text-slate-400">Имя</label>
              <input type="text" value={fullName} onChange={e => setFullName(e.target.value)}
                     className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-white" />
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Организация *</label>
              <OrgCombobox value={orgId || null}
                           onChange={(id) => setOrgId(id || '')}
                           options={orgs}
                           placeholder="Поиск организации…" />
            </div>
            {err && <div className="text-xs text-red-400">{err}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose}
                      className="text-xs px-3 py-1.5 rounded border border-slate-700 text-slate-300 hover:bg-slate-800">
                Отмена
              </button>
              <button type="submit" disabled={busy}
                      className="text-xs px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white font-bold">
                {busy ? <Loader2 size={12} className="animate-spin inline" /> : 'Создать'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function ClientEditModal({ user, orgs, onClose, onSaved }:
  { user: ClientUser; orgs: Organization[]; onClose: () => void; onSaved: () => void }) {
  const [fullName, setFullName] = useState(user.full_name || '');
  const [email, setEmail] = useState(user.email);
  const [phone, setPhone] = useState(user.phone || '');
  const [position, setPosition] = useState(user.position || '');
  const [tgu, setTgu] = useState(user.telegram_username || '');
  const [maxu, setMaxu] = useState(user.max_username || '');
  const [orgId, setOrgId] = useState(user.organization_id || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      // Отправляем только реально изменённые поля — иначе бэкенд может
      // 422'ить из-за валидации старых данных (например, EmailStr на
      // адресе, который сейчас в БД но не проходит строгую проверку).
      const patch: any = {};
      if (fullName !== (user.full_name || '')) patch.full_name = fullName;
      if (email !== user.email) patch.email = email;
      if (phone !== (user.phone || '')) patch.phone = phone || null;
      if (position !== (user.position || '')) patch.position = position || null;
      if (tgu !== (user.telegram_username || '')) patch.telegram_username = tgu || null;
      if (maxu !== (user.max_username || '')) patch.max_username = maxu || null;
      if (orgId && orgId !== user.organization_id) patch.organization_id = orgId;
      if (Object.keys(patch).length === 0) { onSaved(); return; }
      await clientsApi.patch(user.id, patch);
      onSaved();
    } catch (e: any) {
      setErr(extractErrorMessage(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <form onSubmit={submit}
            className="bg-slate-900 border border-slate-700 rounded-xl p-5 max-w-md w-full space-y-3">
        <h3 className="font-bold text-white">Редактировать клиента</h3>
        <div>
          <label className="text-xs text-slate-400">Имя</label>
          <input value={fullName} onChange={e => setFullName(e.target.value)}
                 className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-white" />
        </div>
        <div>
          <label className="text-xs text-slate-400">Email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                 className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-white" />
        </div>
        <div>
          <label className="text-xs text-slate-400 block mb-1">Организация</label>
          <OrgCombobox value={orgId || null}
                       onChange={(id) => setOrgId(id || '')}
                       options={orgs}
                       placeholder="Поиск организации…"
                       clearable />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-slate-400">Телефон</label>
            <input value={phone} onChange={e => setPhone(e.target.value)}
                   className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-white" />
          </div>
          <div>
            <label className="text-xs text-slate-400">Должность</label>
            <input value={position} onChange={e => setPosition(e.target.value)}
                   className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-white" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-slate-400">Telegram username</label>
            <input value={tgu} onChange={e => setTgu(e.target.value)}
                   className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-white" />
          </div>
          <div>
            <label className="text-xs text-slate-400">MAX username</label>
            <input value={maxu} onChange={e => setMaxu(e.target.value)}
                   className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-white" />
          </div>
        </div>
        {err && <div className="text-xs text-red-400">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose}
                  className="text-xs px-3 py-1.5 rounded border border-slate-700 text-slate-300 hover:bg-slate-800">
            Отмена
          </button>
          <button type="submit" disabled={busy}
                  className="text-xs px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white font-bold">
            {busy ? <Loader2 size={12} className="animate-spin inline" /> : 'Сохранить'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Audit (PostHog-style действия пользователей и клиентов)
// ────────────────────────────────────────────────────────────────────────────

type AuditEvent = {
  id: string;
  ts: string;
  actor_type: string | null;
  actor_id: string | null;
  actor_email: string | null;
  kind: string;
  target_type: string | null;
  target_id: string | null;
  meta: Record<string, any> | null;
  ip: string | null;
  user_agent: string | null;
};

type KindStat = { kind: string; count: number };

type UserAnalytics = {
  actor_id: string | null;
  actor_email: string | null;
  actor_full_name: string | null;
  actor_type: string | null;
  organization_id: string | null;
  organization_name: string | null;
  total_events: number;
  page_views: number;
  actions: number;
  last_seen: string | null;
  first_seen: string | null;
  sessions_count: number;
  total_duration_sec: number;
  is_online: boolean;
  temperature: 'hot' | 'warm' | 'cool' | 'cold';
  stale_days: number | null;
  top_pages: { path: string; count: number }[];
  top_kinds: { kind: string; count: number }[];
};

type Overview = {
  total_events: number;
  unique_users: number;
  sessions_count: number;
  avg_session_sec: number;
  total_page_views: number;
  total_duration_sec: number;
};

type OnlineUser = {
  session_id: string;
  user_id: string;
  user_email: string | null;
  user_full_name: string | null;
  user_type: string | null;
  organization_id: string | null;
  organization_name: string | null;
  start_at: string;
  last_activity_at: string;
  duration_sec: number;
  page_views: number;
  ip: string | null;
};

function fmtDuration(sec: number): string {
  if (!sec || sec < 0) return '—';
  if (sec < 60) return `${sec}с`;
  if (sec < 3600) return `${Math.floor(sec / 60)}м`;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return m > 0 ? `${h}ч ${m}м` : `${h}ч`;
}

const TEMP_COLOR: Record<string, string> = {
  hot: 'bg-red-900/40 text-red-300 border-red-700',
  warm: 'bg-orange-900/40 text-orange-300 border-orange-700',
  cool: 'bg-blue-900/40 text-blue-300 border-blue-700',
  cold: 'bg-slate-800 text-slate-500 border-slate-700',
};
const TEMP_LABEL: Record<string, string> = {
  hot: 'горячий', warm: 'тёплый', cool: 'остывает', cold: 'холодный',
};

function OverviewCard({ label, value, hint, accent }:
  { label: string; value: number | string; hint?: string; accent?: 'green' | 'blue' | 'purple' | 'orange' | 'slate' }) {
  const c = {
    green: 'border-green-900/60 bg-green-950/30',
    blue: 'border-blue-900/60 bg-blue-950/30',
    purple: 'border-purple-900/60 bg-purple-950/30',
    orange: 'border-orange-900/60 bg-orange-950/30',
    slate: 'border-slate-800 bg-slate-900',
  }[accent || 'slate'];
  return (
    <div className={`rounded-lg border ${c} p-3`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{label}</div>
      <div className="text-xl font-bold text-white mt-1">{value}</div>
      {hint && <div className="text-[10px] text-slate-500 mt-1 truncate" title={hint}>{hint}</div>}
    </div>
  );
}

function AuditEditor() {
  const [tab, setTab] = useState<'events' | 'users'>('users');
  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl p-5">
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <h2 className="text-xl font-bold text-white">Аналитика действий</h2>
        <div className="flex gap-1">
          <button onClick={() => setTab('users')}
                  className={`text-xs px-3 py-1.5 rounded font-bold ${tab === 'users' ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-slate-200'}`}>
            По пользователям
          </button>
          <button onClick={() => setTab('events')}
                  className={`text-xs px-3 py-1.5 rounded font-bold ${tab === 'events' ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-slate-200'}`}>
            Лог событий
          </button>
        </div>
      </div>
      {tab === 'users' ? <AuditByUser /> : <AuditEventsLog />}
    </section>
  );
}

function AuditByUser() {
  const [items, setItems] = useState<UserAnalytics[]>([]);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState(7);
  const [actorType, setActorType] = useState<string>('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const params: any = { period_days: period, limit: 200 };
      if (actorType) params.actor_type = actorType;
      const [byUserR, ovR, onR] = await Promise.all([
        api.get('/api/v1/audit/by-user', { params }),
        api.get('/api/v1/audit/overview', { params: { period_days: period } }),
        api.get('/api/v1/audit/online'),
      ]);
      setItems(byUserR.data || []);
      setOverview(ovR.data || null);
      setOnlineUsers(onR.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [period, actorType]);

  // Авто-рефреш Online каждые 30 секунд
  useEffect(() => {
    const t = window.setInterval(() => {
      api.get('/api/v1/audit/online').then(r => setOnlineUsers(r.data || [])).catch(() => {});
    }, 30_000);
    return () => window.clearInterval(t);
  }, []);

  return (
    <>
      {/* Overview карточки */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
        <OverviewCard label="Сейчас онлайн" value={onlineUsers.length}
                      hint={onlineUsers.length > 0
                        ? onlineUsers.map(o => o.user_email).filter(Boolean).slice(0, 3).join(', ')
                        : 'никого'} accent="green" />
        <OverviewCard label="Уникальные пользователи" value={overview?.unique_users || 0} accent="blue" />
        <OverviewCard label="Сессий за период" value={overview?.sessions_count || 0} accent="purple" />
        <OverviewCard label="Среднее время сессии" value={fmtDuration(overview?.avg_session_sec || 0)} accent="orange" />
        <OverviewCard label="Всего событий" value={(overview?.total_events || 0).toLocaleString('ru-RU')} accent="slate" />
      </div>

      {/* Виджет: кто прямо сейчас */}
      {onlineUsers.length > 0 && (
        <div className="bg-green-950/30 border border-green-900/50 rounded-lg p-3 mb-3">
          <div className="text-[10px] uppercase tracking-wider text-green-400 font-bold mb-2">
            Сейчас в системе ({onlineUsers.length})
          </div>
          <div className="flex flex-wrap gap-2">
            {onlineUsers.map(o => (
              <div key={o.session_id}
                   className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                <span className="text-white">{o.user_full_name || o.user_email || o.user_id}</span>
                {o.user_email && o.user_full_name && (
                  <span className="text-slate-500 font-mono text-[10px]">{o.user_email}</span>
                )}
                {o.user_type && <span className="text-slate-500">({o.user_type})</span>}
                {o.organization_name && (
                  <a href={`/organizations/${o.organization_id}`}
                     onClick={e => e.stopPropagation()}
                     className="text-blue-400 hover:text-blue-300">{o.organization_name}</a>
                )}
                <span className="text-slate-500">— {fmtDuration(o.duration_sec)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-slate-500 mb-3">
        Кто из сотрудников и клиентов сколько раз заходил, на каких страницах и какие действия совершал.
        Клик на строку — детализация. Колонка «Температура»: горячий ≤1дн / тёплый ≤7дн / остывает ≤30дн / холодный.
      </p>
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <label className="text-xs text-slate-400">
          Период
          <select className="block bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-white"
                  value={period} onChange={e => setPeriod(Number(e.target.value))}>
            <option value={1}>сутки</option>
            <option value={7}>7 дней</option>
            <option value={30}>30 дней</option>
            <option value={90}>90 дней</option>
            <option value={365}>год</option>
          </select>
        </label>
        <label className="text-xs text-slate-400">
          Тип
          <select className="block bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-white"
                  value={actorType} onChange={e => setActorType(e.target.value)}>
            <option value="">все</option>
            <option value="staff">сотрудники</option>
            <option value="client">клиенты</option>
          </select>
        </label>
        <button className="bg-blue-700 hover:bg-blue-600 text-white text-xs font-bold px-3 py-2 rounded"
                onClick={() => void load()} disabled={loading}>
          {loading ? <Loader2 size={12} className="animate-spin inline" /> : 'Обновить'}
        </button>
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm py-8 text-center"><Loader2 className="inline animate-spin mr-2" size={14} />загружаем…</div>
      ) : items.length === 0 ? (
        <div className="text-slate-500 text-sm py-8 text-center">за период активности не было</div>
      ) : (
        <table className="min-w-full text-xs">
          <thead className="text-slate-500 uppercase border-b border-slate-800">
            <tr>
              <th className="text-left py-2 px-2 font-bold">Пользователь</th>
              <th className="text-left py-2 px-2 font-bold">Тип</th>
              <th className="text-left py-2 px-2 font-bold">Организация</th>
              <th className="text-right py-2 px-2 font-bold">Сессий</th>
              <th className="text-right py-2 px-2 font-bold">Время</th>
              <th className="text-right py-2 px-2 font-bold">Просмотров</th>
              <th className="text-right py-2 px-2 font-bold">Действий</th>
              <th className="text-left py-2 px-2 font-bold">Темп.</th>
              <th className="text-left py-2 px-2 font-bold">Последний раз</th>
            </tr>
          </thead>
          <tbody>
            {items.map(u => {
              const key = u.actor_email || u.actor_id || '?';
              const isOpen = expanded === key;
              return (
                <>
                  <tr key={key} onClick={() => setExpanded(isOpen ? null : key)}
                      className="border-b border-slate-800/50 hover:bg-slate-800/30 cursor-pointer">
                    <td className="py-1.5 px-2">
                      <div className="flex items-center gap-1.5">
                        {u.is_online && <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" title="онлайн" />}
                        <div>
                          <div className="text-white">{u.actor_full_name || u.actor_email || u.actor_id || '—'}</div>
                          {u.actor_full_name && u.actor_email && (
                            <div className="text-[10px] text-slate-500 font-mono">{u.actor_email}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-1.5 px-2">
                      {u.actor_type === 'staff'
                        ? <span className="text-blue-400">сотрудник</span>
                        : u.actor_type === 'client'
                          ? <span className="text-green-400">клиент</span>
                          : <span className="text-slate-500">—</span>}
                    </td>
                    <td className="py-1.5 px-2 text-slate-400">
                      {u.organization_name
                        ? <a href={`/organizations/${u.organization_id}`}
                             onClick={e => e.stopPropagation()}
                             className="text-blue-400 hover:text-blue-300">{u.organization_name}</a>
                        : <span className="text-slate-600">—</span>}
                    </td>
                    <td className="py-1.5 px-2 text-right text-white font-bold">{u.sessions_count}</td>
                    <td className="py-1.5 px-2 text-right text-slate-300">{fmtDuration(u.total_duration_sec)}</td>
                    <td className="py-1.5 px-2 text-right text-slate-300">{u.page_views}</td>
                    <td className="py-1.5 px-2 text-right text-slate-300">{u.actions}</td>
                    <td className="py-1.5 px-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${TEMP_COLOR[u.temperature] || TEMP_COLOR.cold}`}>
                        {TEMP_LABEL[u.temperature] || u.temperature}
                      </span>
                      {u.stale_days !== null && u.stale_days > 0 && (
                        <span className="ml-1 text-[9px] text-slate-600">{u.stale_days}д</span>
                      )}
                    </td>
                    <td className="py-1.5 px-2 text-slate-400 whitespace-nowrap">
                      {u.last_seen ? new Date(u.last_seen).toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' }) : '—'}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr key={key + '_d'}>
                      <td colSpan={9} className="bg-slate-800/30 p-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <div className="text-[10px] uppercase text-slate-500 font-bold mb-1">Топ страниц</div>
                            {u.top_pages.length === 0 ? <div className="text-slate-600 text-xs">—</div> : (
                              <table className="w-full text-xs">
                                <tbody>
                                  {u.top_pages.map(p => (
                                    <tr key={p.path}>
                                      <td className="py-0.5 px-2 text-slate-300 font-mono">{p.path}</td>
                                      <td className="py-0.5 px-2 text-right text-white font-bold">{p.count}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                          <div>
                            <div className="text-[10px] uppercase text-slate-500 font-bold mb-1">Топ действий</div>
                            {u.top_kinds.length === 0 ? <div className="text-slate-600 text-xs">—</div> : (
                              <table className="w-full text-xs">
                                <tbody>
                                  {u.top_kinds.map(k => (
                                    <tr key={k.kind}>
                                      <td className="py-0.5 px-2 text-slate-300 font-mono">{k.kind}</td>
                                      <td className="py-0.5 px-2 text-right text-white font-bold">{k.count}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </div>
                        <div className="text-[10px] text-slate-500 mt-2">
                          Первый раз: {u.first_seen ? new Date(u.first_seen).toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' }) : '—'}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
}

function AuditEventsLog() {
  const [items, setItems] = useState<AuditEvent[]>([]);
  const [kinds, setKinds] = useState<KindStat[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState(7);
  const [kindFilter, setKindFilter] = useState<string>('');
  const [actorFilter, setActorFilter] = useState<string>('');

  const load = async () => {
    setLoading(true);
    try {
      const params: any = { period_days: period, limit: 300 };
      if (kindFilter) params.kind = kindFilter;
      if (actorFilter) params.actor = actorFilter;
      const [evRes, kindsRes] = await Promise.all([
        api.get('/api/v1/audit/events', { params }),
        api.get('/api/v1/audit/kinds', { params: { period_days: period } }),
      ]);
      setItems(evRes.data.items || []);
      setTotal(evRes.data.total || 0);
      setKinds(kindsRes.data || []);
    } catch (e) {
      console.warn('audit load failed', e);
    } finally {
      setLoading(false);
    }
  };

  const [groupRepeats, setGroupRepeats] = useState(true);

  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [period, kindFilter, actorFilter]);

  // Группировка: схлопывает подряд идущие (actor+kind+target) события в ±60 секунд в одну строку.
  type GroupedRow = { lead: AuditEvent; count: number; lastTs: string };
  const grouped: GroupedRow[] = (() => {
    if (!groupRepeats) return items.map(e => ({ lead: e, count: 1, lastTs: e.ts }));
    const out: GroupedRow[] = [];
    for (const e of items) {
      const last = out[out.length - 1];
      if (last) {
        const sameActor = (last.lead.actor_email || last.lead.actor_id) === (e.actor_email || e.actor_id);
        const sameKind = last.lead.kind === e.kind;
        const sameTarget = last.lead.target_type === e.target_type && last.lead.target_id === e.target_id;
        const dt = Math.abs(new Date(last.lead.ts).getTime() - new Date(e.ts).getTime());
        if (sameActor && sameKind && sameTarget && dt <= 60_000) {
          last.count += 1;
          last.lastTs = e.ts; // самое раннее остаётся в lead (ts), здесь храним lastTs (хвостовое для отображения «×N до …»)
          continue;
        }
      }
      out.push({ lead: e, count: 1, lastTs: e.ts });
    }
    return out;
  })();

  return (
    <>
      <p className="text-xs text-slate-500 mb-3">
        Записываются: вход/неудачный вход, создание тикета, ответ в тикете, page-view, действия над организациями.
        Видно только администратору. Всего за период: <span className="text-slate-300">{total.toLocaleString('ru-RU')}</span>.
      </p>

      {/* Фильтры */}
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <label className="text-xs text-slate-400">
          Период
          <select className="block bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-white"
                  value={period} onChange={e => setPeriod(Number(e.target.value))}>
            <option value={1}>сутки</option>
            <option value={7}>7 дней</option>
            <option value={30}>30 дней</option>
            <option value={90}>90 дней</option>
            <option value={365}>год</option>
          </select>
        </label>
        <label className="text-xs text-slate-400">
          Вид
          <select className="block bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-white min-w-[200px]"
                  value={kindFilter} onChange={e => setKindFilter(e.target.value)}>
            <option value="">все</option>
            {kinds.map(k => (
              <option key={k.kind} value={k.kind}>{k.kind} ({k.count})</option>
            ))}
          </select>
        </label>
        <label className="text-xs text-slate-400">
          Актор (email или ID)
          <input className="block bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-white"
                 value={actorFilter} onChange={e => setActorFilter(e.target.value)}
                 placeholder="user@..." />
        </label>
        <label className="text-xs text-slate-400 flex items-center gap-1 self-end pb-1">
          <input type="checkbox" checked={groupRepeats} onChange={e => setGroupRepeats(e.target.checked)} />
          Сворачивать повторы
        </label>
        <button className="bg-blue-700 hover:bg-blue-600 text-white text-xs font-bold px-3 py-2 rounded"
                onClick={() => void load()} disabled={loading}>
          {loading ? <Loader2 size={12} className="animate-spin inline" /> : 'Обновить'}
        </button>
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm py-8 text-center"><Loader2 className="inline animate-spin mr-2" size={14} />загружаем…</div>
      ) : items.length === 0 ? (
        <div className="text-slate-500 text-sm py-8 text-center">за период ничего не записано</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="text-slate-500 uppercase border-b border-slate-800">
              <tr>
                <th className="text-left py-2 px-2 font-bold">Время</th>
                <th className="text-left py-2 px-2 font-bold">Вид</th>
                <th className="text-left py-2 px-2 font-bold">Актор</th>
                <th className="text-left py-2 px-2 font-bold">Цель</th>
                <th className="text-left py-2 px-2 font-bold">IP</th>
                <th className="text-left py-2 px-2 font-bold">Контекст</th>
              </tr>
            </thead>
            <tbody>
              {grouped.map(g => {
                const e = g.lead;
                return (
                <tr key={e.id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                  <td className="py-1.5 px-2 text-slate-300 whitespace-nowrap">
                    {new Date(e.ts).toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' })}
                  </td>
                  <td className="py-1.5 px-2 text-white font-mono">
                    {e.kind}
                    {g.count > 1 && <span className="ml-2 text-[10px] text-blue-300 font-bold bg-blue-900/30 px-1 rounded">×{g.count}</span>}
                  </td>
                  <td className="py-1.5 px-2 text-slate-300">
                    {e.actor_email || e.actor_id || <span className="text-slate-600">—</span>}
                    {e.actor_type && <span className="text-slate-600 ml-1">({e.actor_type})</span>}
                  </td>
                  <td className="py-1.5 px-2 text-slate-400">
                    {e.target_type ? `${e.target_type}#${e.target_id}` : <span className="text-slate-600">—</span>}
                  </td>
                  <td className="py-1.5 px-2 text-slate-500 font-mono">{e.ip || '—'}</td>
                  <td className="py-1.5 px-2 text-slate-400 max-w-md truncate font-mono">
                    {e.meta ? JSON.stringify(e.meta) : '—'}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
